/**
 * ============================================================
 * DISEÑADOR VISUAL DE BOT DE WHATSAPP v2.0
 * ============================================================
 * 
 * Editor de flujos tipo drag & drop para diseñar bots de WhatsApp.
 * 
 * CARACTERÍSTICAS:
 * - 22 tipos de nodos en 4 categorías
 * - Sistema de variables globales con interpolación {{var}}
 * - Canvas SVG con zoom, pan y conexiones Bezier
 * - Panel de propiedades contextual
 * - Import/Export JSON completo
 * - Export texto plano para integración externa
 * - Soporte multilenguaje (ES, EN, PT)
 * - Comentarios en español para futuras mejoras
 * 
 * TIPOS DE NODO POR CATEGORÍA:
 * - Mensajes: Enviar Mensaje, Media, Despedida
 * - WhatsApp Esenciales: Reply Buttons, List Buttons, 
 *   Keyword Options, Send Template, Contact Subscribe
 * - Preguntas: Pregunta, Email, Número, Teléfono, Fecha,
 *   Archivo, Dirección, URL  
 * - Lógica: Condiciones, Set Field, Keyword Jump, 
 *   Global Keywords, Fórmulas, Jump To, Lead Scoring, Goal, A/B Test
 * 
 * SISTEMA DE VARIABLES:
 * - Crear variables desde el panel o desde cualquier nodo
 * - Interpolar con {{nombre}} en cualquier campo de texto
 * - Cada nodo Question/WhatsApp puede guardar respuesta en variable
 * - Variables accesibles globalmente en todo el flujo
 * 
 * @version 2.0.0
 */

import { useState, useCallback, useRef, useEffect } from "react";
import JSZip from "jszip";
import { saveFlow } from "./storage";

// ============================================================
// SISTEMA DE INTERNACIONALIZACIÓN (i18n)
// Para agregar un idioma: añadir nuevo objeto al diccionario
// con las mismas claves que "es"
// ============================================================
const I18N = {
  es: {
    "cat.messages": "Mensajes", "cat.whatsapp": "WhatsApp Esenciales",
    "cat.questions": "Preguntas", "cat.logic": "Lógica", "cat.integrations": "Integraciones",
    "node.startingPoint": "Punto de Inicio", "node.startingPoint.desc": "Donde comienza tu bot",
    "node.sendMessage": "Enviar Mensaje", "node.sendMessage.desc": "Envía un mensaje de texto",
    "node.media": "Media", "node.media.desc": "Envía imagen, video o documento",
    "node.goodbye": "Mensaje de Despedida", "node.goodbye.desc": "Finaliza la conversación",
    "node.replyButtons": "Botones de Respuesta", "node.replyButtons.desc": "Hasta 3 opciones rápidas",
    "node.listButtons": "Lista de Botones", "node.listButtons.desc": "Menú de selección (hasta 10)",
    "node.keywordOptions": "Opciones por Palabra", "node.keywordOptions.desc": "Respuesta según keyword",
    "node.sendTemplate": "Enviar Plantilla", "node.sendTemplate.desc": "Plantilla aprobada de WA",
    "node.contactSubscribe": "Suscribir Contacto", "node.contactSubscribe.desc": "Suscribe al contacto",
    "node.askQuestion": "Hacer Pregunta", "node.askQuestion.desc": "Pregunta de texto libre",
    "node.askEmail": "Pedir Email", "node.askEmail.desc": "Solicita correo electrónico",
    "node.askNumber": "Pedir Número", "node.askNumber.desc": "Solicita un número",
    "node.askPhone": "Pedir Teléfono", "node.askPhone.desc": "Solicita número telefónico",
    "node.askDate": "Pedir Fecha", "node.askDate.desc": "Solicita una fecha",
    "node.askFile": "Pedir Archivo", "node.askFile.desc": "Solicita un archivo",
    "node.askAddress": "Pedir Dirección", "node.askAddress.desc": "Solicita una dirección",
    "node.askUrl": "Pedir URL", "node.askUrl.desc": "Solicita una URL",
    "node.conditions": "Condiciones", "node.conditions.desc": "Bifurcación por condición",
    "node.setField": "Asignar Variable", "node.setField.desc": "Establece valor de variable",
    "node.keywordJump": "Salto por Palabra", "node.keywordJump.desc": "Salta según keyword",
    "node.globalKeywords": "Palabras Globales", "node.globalKeywords.desc": "Keywords globales del bot",
    "node.formulas": "Fórmulas", "node.formulas.desc": "Calcula con variables",
    "node.jumpTo": "Saltar A", "node.jumpTo.desc": "Salta a otro nodo",
    "node.leadScoring": "Lead Scoring", "node.leadScoring.desc": "Puntaje del contacto",
    "node.goal": "Objetivo", "node.goal.desc": "Marca objetivo cumplido",
    "node.abTest": "Test A/B", "node.abTest.desc": "Divide tráfico en variantes",
    "node.integrationEntry": "Entrada", "node.integrationEntry.desc": "Entrada al bloque de integración",
    "node.apiCall": "Integración por API", "node.apiCall.desc": "Llama a un endpoint HTTP externo",
    "node.dbQuery": "Consulta a BD", "node.dbQuery.desc": "Ejecuta una consulta a base de datos",
    "node.integrationExit": "Salida", "node.integrationExit.desc": "Salida del bloque de integración",
    "cat.aiGenerator": "Integración Generador",
    "node.textMenu": "Menú por Texto", "node.textMenu.desc": "Menú USSD con hasta 15 opciones por palabra clave",
    "node.transferAgent": "Transferir a Agente", "node.transferAgent.desc": "Deriva a un agente humano (ACD)",
    "node.acdSkill": "Preferencia ACD", "node.acdSkill.desc": "Setea habilidad/cola ACD antes de transferir",
    "node.groqChat": "Agente Groq IA", "node.groqChat.desc": "Chat con IA gratuita vía Groq (llama, mixtral)",
    "node.loopControl": "Control de Ciclo", "node.loopControl.desc": "Limita repeticiones de un bucle",
    "node.aiBot": "Bot IA Generativa", "node.aiBot.desc": "Integración con asistente de IA (OpenAI Assistants)",
    "cat.misc": "Misceláneas",
    "node.calendar": "Calendario", "node.calendar.desc": "Verifica franja horaria y feriados",
    "node.flowControl": "Control de Flujo", "node.flowControl.desc": "Extrae valores del recorrido y los convierte en variables",
    "ui.variables": "Variables", "ui.export": "Exportar Bot", "ui.import": "Importar Bot",
    "ui.exportZip": "Exportar ZIP", "ui.importZip": "Importar ZIP",
    "ui.exportText": "Exportar Texto", "ui.addVariable": "Nueva Variable",
    "ui.save": "Guardar", "ui.cancel": "Cancelar", "ui.delete": "Eliminar",
    "ui.close": "Cerrar", "ui.properties": "Propiedades", "ui.message": "Mensaje",
    "ui.buttons": "Botones", "ui.addButton": "Agregar Botón", "ui.options": "Opciones",
    "ui.addOption": "Agregar Opción", "ui.saveVar": "Guardar en variable",
    "ui.selectVar": "Seleccionar variable...", "ui.createVar": "Crear nueva...",
    "ui.templateName": "Nombre de plantilla", "ui.mediaUrl": "URL del media",
    "ui.mediaType": "Tipo de media", "ui.value": "Valor", "ui.variable": "Variable",
    "ui.operator": "Operador", "ui.formula": "Fórmula", "ui.targetNode": "Nodo destino",
    "ui.keyword": "Palabra clave", "ui.keywords": "Palabras clave",
    "ui.addKeyword": "Agregar keyword", "ui.score": "Puntaje", "ui.percentage": "Porcentaje",
    "ui.search": "Buscar nodo...", "ui.dragHint": "Arrastra nodos al canvas",
    "ui.anyOfAbove": "Cualquiera de los anteriores", "ui.header": "Encabezado",
    "ui.footer": "Pie de mensaje", "ui.body": "Cuerpo del mensaje",
    "ui.section": "Sección", "ui.addSection": "Agregar sección",
    "ui.resultVar": "Variable resultado", "ui.fieldValue": "Valor del campo",
    "ui.language": "Idioma", "ui.errorMsg": "Mensaje de error",
    "ui.noNodes": "Arrastra nodos desde el panel izquierdo",
    "ui.varHint": "Usa {{variable}} para interpolar valores",
    "ui.connectHint": "Conecta nodos arrastrando desde los puertos ●",
  },
  en: {
    "cat.messages": "Messages", "cat.whatsapp": "WhatsApp Essentials",
    "cat.questions": "Questions", "cat.logic": "Logic", "cat.integrations": "Integrations",
    "node.startingPoint": "Starting Point", "node.startingPoint.desc": "Where your bot begins",
    "node.sendMessage": "Send Message", "node.sendMessage.desc": "Send a text message",
    "node.media": "Media", "node.media.desc": "Send image, video or document",
    "node.goodbye": "Goodbye Message", "node.goodbye.desc": "End the conversation",
    "node.replyButtons": "Reply Buttons", "node.replyButtons.desc": "Up to 3 quick options",
    "node.listButtons": "List Buttons", "node.listButtons.desc": "Selection menu (up to 10)",
    "node.keywordOptions": "Keyword Options", "node.keywordOptions.desc": "Response by keyword",
    "node.sendTemplate": "Send Template", "node.sendTemplate.desc": "Approved WA template",
    "node.contactSubscribe": "Contact Subscribe", "node.contactSubscribe.desc": "Subscribe the contact",
    "node.askQuestion": "Ask Question", "node.askQuestion.desc": "Free text question",
    "node.askEmail": "Ask Email", "node.askEmail.desc": "Request an email",
    "node.askNumber": "Ask Number", "node.askNumber.desc": "Request a number",
    "node.askPhone": "Ask Phone", "node.askPhone.desc": "Request phone number",
    "node.askDate": "Ask Date", "node.askDate.desc": "Request a date",
    "node.askFile": "Ask File", "node.askFile.desc": "Request a file",
    "node.askAddress": "Ask Address", "node.askAddress.desc": "Request an address",
    "node.askUrl": "Ask URL", "node.askUrl.desc": "Request a URL",
    "node.conditions": "Conditions", "node.conditions.desc": "Branch by condition",
    "node.setField": "Set Field", "node.setField.desc": "Set variable value",
    "node.keywordJump": "Keyword Jump", "node.keywordJump.desc": "Jump by keyword",
    "node.globalKeywords": "Global Keywords", "node.globalKeywords.desc": "Global bot keywords",
    "node.formulas": "Formulas", "node.formulas.desc": "Calculate with variables",
    "node.jumpTo": "Jump To", "node.jumpTo.desc": "Jump to another node",
    "node.leadScoring": "Lead Scoring", "node.leadScoring.desc": "Contact scoring",
    "node.goal": "Goal", "node.goal.desc": "Mark goal achieved",
    "node.abTest": "A/B Test", "node.abTest.desc": "Split traffic into variants",
    "node.integrationEntry": "Entry", "node.integrationEntry.desc": "Integration block entry",
    "node.apiCall": "API Integration", "node.apiCall.desc": "Call an external HTTP endpoint",
    "node.dbQuery": "DB Query", "node.dbQuery.desc": "Execute a database query",
    "node.integrationExit": "Exit", "node.integrationExit.desc": "Integration block exit",
    "cat.aiGenerator": "AI Generator Integration",
    "node.textMenu": "Text Menu", "node.textMenu.desc": "USSD menu with up to 15 keyword options",
    "node.transferAgent": "Transfer to Agent", "node.transferAgent.desc": "Route to a human agent (ACD)",
    "node.acdSkill": "ACD Skill Preference", "node.acdSkill.desc": "Sets ACD skill/queue before transferring",
    "node.groqChat": "Groq AI Agent", "node.groqChat.desc": "Free AI chat via Groq (llama, mixtral)",
    "node.loopControl": "Loop Control", "node.loopControl.desc": "Limits loop repetitions",
    "node.aiBot": "Generative AI Bot", "node.aiBot.desc": "OpenAI Assistants integration",
    "cat.misc": "Miscellaneous",
    "node.calendar": "Calendar", "node.calendar.desc": "Checks time range and holidays",
    "node.flowControl": "Flow Control", "node.flowControl.desc": "Extracts journey values and converts them to variables",
    "ui.variables": "Variables", "ui.export": "Export Bot", "ui.import": "Import Bot",
    "ui.exportZip": "Export ZIP", "ui.importZip": "Import ZIP",
    "ui.exportText": "Export Text", "ui.addVariable": "New Variable",
    "ui.save": "Save", "ui.cancel": "Cancel", "ui.delete": "Delete",
    "ui.close": "Close", "ui.properties": "Properties", "ui.message": "Message",
    "ui.buttons": "Buttons", "ui.addButton": "Add Button", "ui.options": "Options",
    "ui.addOption": "Add Option", "ui.saveVar": "Save to variable",
    "ui.selectVar": "Select variable...", "ui.createVar": "Create new...",
    "ui.templateName": "Template name", "ui.mediaUrl": "Media URL",
    "ui.mediaType": "Media type", "ui.value": "Value", "ui.variable": "Variable",
    "ui.operator": "Operator", "ui.formula": "Formula", "ui.targetNode": "Target node",
    "ui.keyword": "Keyword", "ui.keywords": "Keywords",
    "ui.addKeyword": "Add keyword", "ui.score": "Score", "ui.percentage": "Percentage",
    "ui.search": "Search node...", "ui.dragHint": "Drag nodes to canvas",
    "ui.anyOfAbove": "Any of the above", "ui.header": "Header",
    "ui.footer": "Footer", "ui.body": "Message body",
    "ui.section": "Section", "ui.addSection": "Add section",
    "ui.resultVar": "Result variable", "ui.fieldValue": "Field value",
    "ui.language": "Language", "ui.errorMsg": "Error message",
    "ui.noNodes": "Drag nodes from the left panel",
    "ui.varHint": "Use {{variable}} to interpolate values",
    "ui.connectHint": "Connect nodes by dragging from ports ●",
  },
  pt: {
    "cat.messages": "Mensagens", "cat.whatsapp": "WhatsApp Essenciais",
    "cat.questions": "Perguntas", "cat.logic": "Lógica", "cat.integrations": "Integrações",
    "node.startingPoint": "Ponto de Início", "node.startingPoint.desc": "Onde começa seu bot",
    "node.sendMessage": "Enviar Mensagem", "node.sendMessage.desc": "Envia uma mensagem de texto",
    "node.media": "Mídia", "node.media.desc": "Envia imagem, vídeo ou documento",
    "node.goodbye": "Mensagem de Despedida", "node.goodbye.desc": "Encerra a conversa",
    "node.replyButtons": "Botões de Resposta", "node.replyButtons.desc": "Até 3 opções rápidas",
    "node.listButtons": "Lista de Botões", "node.listButtons.desc": "Menu de seleção (até 10)",
    "node.keywordOptions": "Opções por Palavra", "node.keywordOptions.desc": "Resposta por keyword",
    "node.sendTemplate": "Enviar Template", "node.sendTemplate.desc": "Template aprovado do WA",
    "node.contactSubscribe": "Inscrever Contato", "node.contactSubscribe.desc": "Inscreve o contato",
    "node.askQuestion": "Fazer Pergunta", "node.askQuestion.desc": "Pergunta de texto livre",
    "node.askEmail": "Pedir Email", "node.askEmail.desc": "Solicita email",
    "node.askNumber": "Pedir Número", "node.askNumber.desc": "Solicita um número",
    "node.askPhone": "Pedir Telefone", "node.askPhone.desc": "Solicita telefone",
    "node.askDate": "Pedir Data", "node.askDate.desc": "Solicita uma data",
    "node.askFile": "Pedir Arquivo", "node.askFile.desc": "Solicita um arquivo",
    "node.askAddress": "Pedir Endereço", "node.askAddress.desc": "Solicita um endereço",
    "node.askUrl": "Pedir URL", "node.askUrl.desc": "Solicita uma URL",
    "node.conditions": "Condições", "node.conditions.desc": "Bifurcação por condição",
    "node.setField": "Atribuir Variável", "node.setField.desc": "Define valor da variável",
    "node.keywordJump": "Salto por Palavra", "node.keywordJump.desc": "Salta por keyword",
    "node.globalKeywords": "Palavras Globais", "node.globalKeywords.desc": "Keywords globais do bot",
    "node.formulas": "Fórmulas", "node.formulas.desc": "Calcula com variáveis",
    "node.jumpTo": "Saltar Para", "node.jumpTo.desc": "Salta para outro nó",
    "node.leadScoring": "Lead Scoring", "node.leadScoring.desc": "Pontuação do contato",
    "node.goal": "Objetivo", "node.goal.desc": "Marca objetivo cumprido",
    "node.abTest": "Teste A/B", "node.abTest.desc": "Divide tráfego em variantes",
    "node.integrationEntry": "Entrada", "node.integrationEntry.desc": "Entrada do bloco de integração",
    "node.apiCall": "Integração por API", "node.apiCall.desc": "Chama um endpoint HTTP externo",
    "node.dbQuery": "Consulta BD", "node.dbQuery.desc": "Executa uma consulta ao banco de dados",
    "node.integrationExit": "Saída", "node.integrationExit.desc": "Saída do bloco de integração",
    "cat.aiGenerator": "Integração Geradora",
    "node.textMenu": "Menu por Texto", "node.textMenu.desc": "Menu USSD com até 15 opções por palavra-chave",
    "node.transferAgent": "Transferir para Agente", "node.transferAgent.desc": "Encaminha para agente humano (ACD)",
    "node.acdSkill": "Preferência ACD", "node.acdSkill.desc": "Define habilidade/fila ACD antes de transferir",
    "node.groqChat": "Agente Groq IA", "node.groqChat.desc": "Chat com IA gratuita via Groq (llama, mixtral)",
    "node.loopControl": "Controle de Ciclo", "node.loopControl.desc": "Limita repetições de um laço",
    "node.aiBot": "Bot IA Generativa", "node.aiBot.desc": "Integração com assistente de IA (OpenAI Assistants)",
    "cat.misc": "Miscelâneas",
    "node.calendar": "Calendário", "node.calendar.desc": "Verifica faixa horária e feriados",
    "node.flowControl": "Controle de Fluxo", "node.flowControl.desc": "Extrai valores do percurso e os converte em variáveis",
    "ui.variables": "Variáveis", "ui.export": "Exportar Bot", "ui.import": "Importar Bot",
    "ui.exportZip": "Exportar ZIP", "ui.importZip": "Importar ZIP",
    "ui.exportText": "Exportar Texto", "ui.addVariable": "Nova Variável",
    "ui.save": "Salvar", "ui.cancel": "Cancelar", "ui.delete": "Excluir",
    "ui.close": "Fechar", "ui.properties": "Propriedades", "ui.message": "Mensagem",
    "ui.buttons": "Botões", "ui.addButton": "Adicionar Botão", "ui.options": "Opções",
    "ui.addOption": "Adicionar Opção", "ui.saveVar": "Salvar na variável",
    "ui.selectVar": "Selecionar variável...", "ui.createVar": "Criar nova...",
    "ui.templateName": "Nome do template", "ui.mediaUrl": "URL da mídia",
    "ui.mediaType": "Tipo de mídia", "ui.value": "Valor", "ui.variable": "Variável",
    "ui.operator": "Operador", "ui.formula": "Fórmula", "ui.targetNode": "Nó destino",
    "ui.keyword": "Palavra-chave", "ui.keywords": "Palavras-chave",
    "ui.addKeyword": "Adicionar keyword", "ui.score": "Pontuação", "ui.percentage": "Porcentagem",
    "ui.search": "Buscar nó...", "ui.dragHint": "Arraste nós para o canvas",
    "ui.anyOfAbove": "Qualquer um dos anteriores", "ui.header": "Cabeçalho",
    "ui.footer": "Rodapé", "ui.body": "Corpo da mensagem",
    "ui.section": "Seção", "ui.addSection": "Adicionar seção",
    "ui.resultVar": "Variável resultado", "ui.fieldValue": "Valor do campo",
    "ui.language": "Idioma", "ui.errorMsg": "Mensagem de erro",
    "ui.noNodes": "Arraste nós do painel esquerdo",
    "ui.varHint": "Use {{variável}} para interpolar valores",
    "ui.connectHint": "Conecte nós arrastando dos portos ●",
  },
};

// Función de traducción
const useT = (lang) => useCallback((k) => I18N[lang]?.[k] || I18N.es[k] || k, [lang]);

// ============================================================
// DEFINICIONES DE TODOS LOS TIPOS DE NODOS
// Cada nodo tiene: categoría, icono, color, puertos de E/S,
// datos por defecto, y si lee/guarda variables
// ============================================================
const NDEFS = {
  startingPoint: {
    cat: "special", icon: "🏁", color: "#64748b",
    outs: ["default"], ins: [],
    data: {}, saves: false, reads: false,
  },
  // --- Mensajes ---
  sendMessage: {
    cat: "messages", icon: "💬", color: "#06b6d4",
    outs: ["default"], ins: ["input"],
    data: { message: "", imageUrl: "" },
    saves: false, reads: true,
  },
  media: {
    cat: "messages", icon: "🖼️", color: "#8b5cf6",
    outs: ["default"], ins: ["input"],
    data: { mediaType: "image", mediaUrl: "", caption: "" },
    saves: false, reads: true,
  },
  goodbye: {
    cat: "messages", icon: "👋", color: "#f59e0b",
    outs: [], ins: ["input"],
    data: { message: "" },
    saves: false, reads: true,
  },
  // --- WhatsApp Esenciales ---
  replyButtons: {
    cat: "whatsapp", icon: "📋", color: "#3b82f6",
    outs: ["btn_0", "btn_1", "btn_2", "any"], ins: ["input"],
    data: {
      headerType: "text", header: "", headerMediaUrl: "", body: "", footer: "",
      buttons: [
        { id: "btn_0", label: "Opción 1" },
        { id: "btn_1", label: "Opción 2" },
        { id: "btn_2", label: "Opción 3" },
      ],
      saveResponseTo: "",
    },
    saves: true, reads: true, maxBtns: 3,
  },
  listButtons: {
    cat: "whatsapp", icon: "📜", color: "#3b82f6",
    outs: ["opt_0", "opt_1", "opt_2", "any"], ins: ["input"],
    data: {
      header: "", body: "", footer: "", buttonText: "Ver opciones",
      sections: [{
        title: "Sección 1",
        options: [
          { id: "opt_0", title: "Opción 1", desc: "" },
          { id: "opt_1", title: "Opción 2", desc: "" },
          { id: "opt_2", title: "Opción 3", desc: "" },
        ],
      }],
      saveResponseTo: "",
    },
    saves: true, reads: true, maxOpts: 10,
  },
  keywordOptions: {
    cat: "whatsapp", icon: "🔤", color: "#3b82f6",
    outs: ["match", "noMatch"], ins: ["input"],
    data: { keywords: [{ keyword: "", response: "" }], saveResponseTo: "" },
    saves: true, reads: true,
  },
  sendTemplate: {
    cat: "whatsapp", icon: "📄", color: "#3b82f6",
    outs: ["default"], ins: ["input"],
    data: { templateName: "", language: "es", headerParams: [], bodyParams: [] },
    saves: false, reads: true,
  },
  contactSubscribe: {
    cat: "whatsapp", icon: "👤", color: "#3b82f6",
    outs: ["default"], ins: ["input"],
    data: { tags: [] },
    saves: false, reads: false,
  },
  // --- Preguntas (todas guardan respuesta en variable) ---
  askQuestion: {
    cat: "questions", icon: "📝", color: "#10b981",
    outs: ["default"], ins: ["input"],
    data: { question: "", saveResponseTo: "", validationType: "any", errorMessage: "" },
    saves: true, reads: true,
  },
  askEmail: {
    cat: "questions", icon: "✉️", color: "#10b981",
    outs: ["default"], ins: ["input"],
    data: { question: "", saveResponseTo: "", errorMessage: "Ingrese un email válido" },
    saves: true, reads: true,
  },
  askNumber: {
    cat: "questions", icon: "🔢", color: "#10b981",
    outs: ["default"], ins: ["input"],
    data: { question: "", saveResponseTo: "", min: null, max: null, errorMessage: "Ingrese un número válido" },
    saves: true, reads: true,
  },
  askPhone: {
    cat: "questions", icon: "📱", color: "#10b981",
    outs: ["default"], ins: ["input"],
    data: { question: "", saveResponseTo: "", errorMessage: "Ingrese un teléfono válido" },
    saves: true, reads: true,
  },
  askDate: {
    cat: "questions", icon: "📅", color: "#10b981",
    outs: ["default"], ins: ["input"],
    data: { question: "", saveResponseTo: "", dateFormat: "DD/MM/YYYY", errorMessage: "Ingrese una fecha válida" },
    saves: true, reads: true,
  },
  askFile: {
    cat: "questions", icon: "📁", color: "#10b981",
    outs: ["default"], ins: ["input"],
    data: { question: "", saveResponseTo: "", allowedTypes: ["image", "document"] },
    saves: true, reads: true,
  },
  askAddress: {
    cat: "questions", icon: "📍", color: "#10b981",
    outs: ["default"], ins: ["input"],
    data: { question: "", saveResponseTo: "" },
    saves: true, reads: true,
  },
  askUrl: {
    cat: "questions", icon: "🌐", color: "#10b981",
    outs: ["default"], ins: ["input"],
    data: { question: "", saveResponseTo: "", errorMessage: "Ingrese una URL válida" },
    saves: true, reads: true,
  },
  // --- Lógica ---
  conditions: {
    cat: "logic", icon: "🔀", color: "#f97316",
    outs: ["true", "false"], ins: ["input"],
    data: { conditions: [{ variable: "", operator: "equals", value: "" }], logicOp: "AND" },
    saves: false, reads: true,
  },
  setField: {
    cat: "logic", icon: "{ }", color: "#f97316",
    outs: ["default"], ins: ["input"],
    data: { variable: "", value: "" },
    saves: true, reads: true,
  },
  keywordJump: {
    cat: "logic", icon: "🔑", color: "#f97316",
    outs: ["match", "noMatch"], ins: ["input"],
    data: { keywords: [], targetNodeId: "" },
    saves: false, reads: true,
  },
  globalKeywords: {
    cat: "logic", icon: "🌍", color: "#f97316",
    outs: ["default"], ins: [],
    data: { keywords: [{ keyword: "", targetNodeId: "" }] },
    saves: false, reads: false,
  },
  formulas: {
    cat: "logic", icon: "⚡", color: "#f97316",
    outs: ["default"], ins: ["input"],
    data: { formula: "", resultVariable: "" },
    saves: true, reads: true,
  },
  jumpTo: {
    cat: "logic", icon: "↗️", color: "#f97316",
    outs: [], ins: ["input"],
    data: { targetNodeId: "" },
    saves: false, reads: false,
  },
  leadScoring: {
    cat: "logic", icon: "🏆", color: "#f97316",
    outs: ["default"], ins: ["input"],
    data: { scoreChange: 0, scoreVariable: "lead_score" },
    saves: true, reads: true,
  },
  goal: {
    cat: "logic", icon: "🚩", color: "#f97316",
    outs: ["default"], ins: ["input"],
    data: { goalName: "" },
    saves: false, reads: false,
  },
  abTest: {
    cat: "logic", icon: "🧪", color: "#f97316",
    outs: ["variantA", "variantB"], ins: ["input"],
    data: { percentageA: 50, saveVariantTo: "" },
    saves: true, reads: false,
  },
  // ── Integrations ──────────────────────────────────────────
  integrationEntry: {
    cat: "integrations", icon: "⤵️", color: "#0ea5e9",
    outs: ["default"], ins: ["input"],
    data: {},
    saves: false, reads: false,
  },
  apiCall: {
    cat: "integrations", icon: "🔌", color: "#0ea5e9",
    outs: ["success", "error"], ins: ["input"],
    data: {
      url: "", method: "GET",
      headers: "{}",
      authType: "none", authValue: "",
      body: "",
      timeout: 10,
      saveResponseTo: "",
    },
    saves: true, reads: false,
  },
  dbQuery: {
    cat: "integrations", icon: "🗄️", color: "#0ea5e9",
    outs: ["success", "error"], ins: ["input"],
    data: {
      dbType: "postgres",
      connectionString: "",
      query: "",
      params: "",
      saveResponseTo: "",
    },
    saves: true, reads: false,
  },
  integrationExit: {
    cat: "integrations", icon: "⤴️", color: "#0ea5e9",
    outs: ["default"], ins: ["input"],
    data: {},
    saves: false, reads: false,
  },
  // ── Integración Generador ─────────────────────────────────
  textMenu: {
    cat: "aiGenerator", icon: "📟", color: "#0d9488",
    outs: ["tm_0", "tm_1", "invalid", "timeout"], ins: ["input"],
    data: {
      message: "",
      options: [
        { id: "tm_0", keyword: "", label: "Opción 1" },
        { id: "tm_1", keyword: "", label: "Opción 2" },
      ],
      retries: 3,
      timeoutSecs: 20,
      saveResponseTo: "",
    },
    saves: true, reads: true,
  },
  transferAgent: {
    cat: "aiGenerator", icon: "🧑‍💼", color: "#dc2626",
    outs: ["transferred", "noOperators"], ins: ["input"],
    data: { queue: "", priority: "normal" },
    saves: false, reads: false,
  },
  acdSkill: {
    cat: "aiGenerator", icon: "🎯", color: "#b45309",
    outs: ["default"], ins: ["input"],
    data: { skill: "", skills: [] },
    saves: false, reads: false,
  },
  groqChat: {
    cat: "aiGenerator", icon: "💬", color: "#7c3aed",
    outs: ["ok", "error"], ins: ["input"],
    data: { outputVariable: "" },
    saves: true, reads: false,
  },
  loopControl: {
    cat: "aiGenerator", icon: "🔁", color: "#7c3aed",
    outs: ["incomplete", "complete"], ins: ["input"],
    data: { variable: "BUCLE", maxCycles: 3 },
    saves: true, reads: true,
  },
  aiBot: {
    cat: "aiGenerator", icon: "🤖", color: "#0891b2",
    outs: ["disconnect", "ok", "needsHuman", "tagDetected", "digitEnd", "noResources", "error"],
    ins: ["input"],
    data: {
      assistantName: "",
      welcomeThread: "",
      outputVariable: "",
      disconnectVar: "",
      assistanceVar: "",
      timeout: 5,
      maxTokens: 250,
      sleepTime: 750,
    },
    saves: true, reads: true,
  },
  calendar: {
    cat: "misc", icon: "📅", color: "#0369a1",
    outs: ["true", "false"], ins: ["input"],
    data: {
      paused: false,
      days: [1, 2, 3, 4, 5],
      timeStart: "08:00",
      timeEnd: "18:00",
      holidays: [],
    },
    saves: false, reads: false,
  },
  flowControl: {
    cat: "misc", icon: "🔀", color: "#7c3aed",
    outs: ["default"], ins: ["input"],
    data: { mappings: [] }, // [{ traceName: "Label del nodo", targetVar: "MI_VARIABLE" }]
    saves: false, reads: false,
  },
};

// Categorías para el sidebar (misma estructura que las imágenes de referencia)
const CATS = [
  { id: "messages", lk: "cat.messages", nodes: ["sendMessage", "media", "goodbye"] },
  { id: "whatsapp", lk: "cat.whatsapp", nodes: ["replyButtons", "listButtons", "keywordOptions", "sendTemplate", "contactSubscribe"] },
  { id: "questions", lk: "cat.questions", nodes: ["askQuestion", "askEmail", "askNumber", "askPhone", "askDate", "askFile", "askAddress", "askUrl"] },
  { id: "logic", lk: "cat.logic", nodes: ["conditions", "setField", "keywordJump", "globalKeywords", "formulas", "jumpTo", "leadScoring", "goal", "abTest"] },
  { id: "integrations", lk: "cat.integrations", nodes: ["integrationEntry", "apiCall", "dbQuery", "integrationExit"] },
  { id: "aiGenerator", lk: "cat.aiGenerator", nodes: ["textMenu", "transferAgent", "acdSkill", "loopControl", "aiBot", "groqChat"] },
  { id: "misc", lk: "cat.misc", nodes: ["calendar", "flowControl"] },
];

// Generadores de IDs únicos
const uid = () => `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const eid = () => `e${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// Dimensiones constantes de los nodos
const NW = 240; // Ancho del nodo
const NH_HEAD = 44; // Alto del header
const PORT_SPC = 30; // Espaciado entre puertos de salida
const PORT_R = 7; // Radio del puerto

// ============================================================
// Componente auxiliar: Input de texto estilizado
// ============================================================
const TInput = ({ label, value, onChange, multi, ph, type, maxLength }) => {
  const len = (value || "").length;
  const nearLimit = maxLength && len > maxLength * 0.85;
  const overLimit  = maxLength && len > maxLength;
  return (
    <div style={{ marginBottom: 10 }}>
      {label && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
          <label style={{ fontSize: 10, color: "#94a3b8" }}>{label}</label>
          {maxLength && (
            <span style={{ fontSize: 9, color: overLimit ? "#ef4444" : nearLimit ? "#f59e0b" : "#475569" }}>
              {len}/{maxLength}
            </span>
          )}
        </div>
      )}
      {multi ? (
        <textarea value={value || ""} onChange={e => onChange(e.target.value)} placeholder={ph}
          maxLength={maxLength} rows={3}
          style={{ ...inputSt, borderColor: overLimit ? "#ef4444" : nearLimit ? "#f59e0b" : "#334155" }} />
      ) : (
        <input type={type || "text"} value={value || ""} onChange={e => onChange(e.target.value)}
          placeholder={ph} maxLength={maxLength}
          style={{ ...inputSt, borderColor: overLimit ? "#ef4444" : nearLimit ? "#f59e0b" : "#334155" }} />
      )}
    </div>
  );
};

// Componente auxiliar: Selector de variable
const VarSel = ({ label, value, onChange, vars, onAddVar, t }) => {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const inputRef = useRef(null);

  const confirmNew = () => {
    if (newName.trim()) { onAddVar(newName.trim()); onChange(newName.trim()); }
    setCreating(false); setNewName("");
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>
        {label || t("ui.saveVar")}
      </label>
      {creating ? (
        <div style={{ display: "flex", gap: 4 }}>
          <input
            ref={inputRef} autoFocus
            value={newName} onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") confirmNew(); if (e.key === "Escape") { setCreating(false); setNewName(""); } }}
            placeholder="nombre_variable"
            style={{ ...inputSt, flex: 1 }}
          />
          <button onClick={confirmNew}
            style={{ padding: "0 10px", background: "#1e40af", border: "none", borderRadius: 5, color: "#fff", cursor: "pointer", fontSize: 12 }}>
            ✓
          </button>
          <button onClick={() => { setCreating(false); setNewName(""); }}
            style={{ padding: "0 8px", background: "#334155", border: "none", borderRadius: 5, color: "#94a3b8", cursor: "pointer", fontSize: 12 }}>
            ✕
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 4 }}>
          <select value={value || ""} onChange={e => onChange(e.target.value)} style={{ ...inputSt, flex: 1 }}>
            <option value="">{t("ui.selectVar")}</option>
            {vars.map(v => <option key={v.id} value={v.name}>{`{{${v.name}}}`}</option>)}
          </select>
          <button onClick={() => { setCreating(true); setTimeout(() => inputRef.current?.focus(), 50); }}
            title="Crear nueva variable"
            style={{ padding: "0 10px", background: "#1e293b", border: "1px solid #334155", borderRadius: 5, color: "#38bdf8", cursor: "pointer", fontSize: 14, flexShrink: 0 }}>
            +
          </button>
        </div>
      )}
    </div>
  );
};

// Componente auxiliar: campo de media con soporte URL + subida local
const MediaUpload = ({ label, value, onChange, accept = "image/*" }) => {
  const fileRef = useRef(null);
  const isDataUrl = value && value.startsWith("data:");

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>{label}</label>}
      <input
        type="text"
        value={isDataUrl ? "(archivo local cargado)" : (value || "")}
        onChange={e => onChange(e.target.value)}
        placeholder="https://..."
        style={inputSt}
        readOnly={isDataUrl}
      />
      <div style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
        <button
          onClick={() => fileRef.current?.click()}
          style={{
            padding: "4px 10px", background: "#1e40af", border: "none",
            borderRadius: 5, color: "white", cursor: "pointer", fontSize: 10,
          }}
        >
          📁 Subir archivo
        </button>
        {value && (
          <button
            onClick={() => onChange("")}
            style={{
              padding: "4px 8px", background: "#7f1d1d", border: "none",
              borderRadius: 5, color: "#fca5a5", cursor: "pointer", fontSize: 10,
            }}
          >
            ✕ Quitar
          </button>
        )}
      </div>
      {isDataUrl && (
        <img
          src={value}
          alt="preview"
          style={{ marginTop: 6, maxWidth: "100%", maxHeight: 120, borderRadius: 6, objectFit: "cover" }}
        />
      )}
      {value && !isDataUrl && value.startsWith("http") && (
        <img
          src={value}
          alt="preview"
          onError={e => { e.target.style.display = "none"; }}
          style={{ marginTop: 6, maxWidth: "100%", maxHeight: 120, borderRadius: 6, objectFit: "cover" }}
        />
      )}
      <input ref={fileRef} type="file" accept={accept} onChange={handleFile} style={{ display: "none" }} />
    </div>
  );
};

// Componente auxiliar: botones para insertar variables en un campo de texto
const VarInsert = ({ value, onChange, vars }) => {
  if (!vars || vars.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4, marginBottom: 6 }}>
      {vars.map(v => (
        <button key={v.id}
          onClick={() => onChange((value || "") + `{{${v.name}}}`)}
          style={{
            fontSize: 9, padding: "2px 7px", background: "#172554",
            border: "1px solid #1e40af", borderRadius: 4,
            color: "#93c5fd", cursor: "pointer", fontFamily: "monospace",
          }}>
          {`{{${v.name}}}`}
        </button>
      ))}
    </div>
  );
};

// Estilos compartidos
const inputSt = {
  width: "100%", padding: "6px 8px", background: "#1e293b",
  border: "1px solid #334155", borderRadius: 6, color: "#e2e8f0",
  fontSize: 12, fontFamily: "'Segoe UI', system-ui, sans-serif",
  outline: "none", resize: "vertical", boxSizing: "border-box",
};

// ============================================================
// INSPECTOR DE FLUJO
// Función pura: recorre el grafo BFS desde startingPoint y genera
// el resumen de pantalla + el JSON estructurado para el ZIP.
// ============================================================

const INSP_PORT_LABELS = {
  default: "→", true: "Verdadero", false: "Falso",
  success: "Éxito", error: "Error", ok: "Finaliza OK",
  disconnect: "Desconexión", needsHuman: "Requiere asistencia",
  noOperators: "Sin operadores", timeout: "Tiempo agotado",
  invalid: "Entrada inválida", complete: "Ciclo completo",
  incomplete: "Ciclo incompleto", transferred: "Transferido",
  noResources: "Sin recursos", tagDetected: "Tag detectado",
  digitEnd: "Dígito finalizador", any: "Cualquier opción",
  match: "Coincidencia", noMatch: "Sin coincidencia",
  variantA: "Variante A", variantB: "Variante B",
};

function inspPortLabel(sp, srcNode) {
  if (INSP_PORT_LABELS[sp]) return INSP_PORT_LABELS[sp];
  // tm_N → keyword de textMenu
  const mTm = sp.match(/^tm_(\d+)$/);
  if (mTm && srcNode?.data?.options) {
    const opt = srcNode.data.options.find(o => o.id === sp);
    if (opt?.keyword) return opt.keyword;
    return `Opción ${mTm[1]}`;
  }
  // opt_N → title de listButtons
  const mOpt = sp.match(/^opt_(\d+)$/);
  if (mOpt && srcNode?.data?.sections) {
    const allOpts = srcNode.data.sections.flatMap(s => s.options || []);
    const opt = allOpts.find(o => o.id === sp);
    if (opt?.title) return opt.title;
    return `Opción ${mOpt[1]}`;
  }
  // btn_N → label de replyButtons
  const mBtn = sp.match(/^btn_(\d+)$/);
  if (mBtn && srcNode?.data?.buttons) {
    const btn = srcNode.data.buttons.find(b => b.id === sp);
    if (btn?.label) return btn.label;
    return `Botón ${mBtn[1]}`;
  }
  return sp;
}

function inspGetLabel(node) {
  const d = node.data || {};
  const trunc = (s, n = 60) => (s && s.length > n) ? s.slice(0, n - 1) + "…" : (s || "");
  switch (node.type) {
    case "sendMessage":
    case "goodbye":      return trunc(d.message);
    case "textMenu":     return trunc(d.message);
    case "listButtons":  return trunc(d.body);
    case "conditions":   return d.conditions?.[0]
      ? `${d.conditions[0].variable} ${d.conditions[0].operator} "${d.conditions[0].value}"`
      : "";
    case "setField":     return `${d.variable} = ${d.value}`;
    case "aiBot":        return d.assistantName || "";
    case "transferAgent":return d.queue || "";
    case "loopControl":  return `${d.variable || "BUCLE"} max:${d.maxCycles || 3}`;
    case "dbQuery":      return d.procedure || d.query || d.dsn || "";
    case "jumpTo":       return d.targetNodeId ? `→ ${d.targetNodeId.slice(0, 8)}` : "";
    case "replyButtons": return trunc(d.body || d.header);
    case "askQuestion":
    case "askEmail":
    case "askNumber":
    case "askPhone":
    case "askDate":
    case "askFile":
    case "askAddress":
    case "askUrl":       return trunc(d.question);
    case "apiCall":      return `${d.method || "GET"} ${trunc(d.url, 40)}`;
    default:             return node.type;
  }
}

function inspDetailLines(node) {
  const d = node.data || {};
  const lines = [];
  switch (node.type) {
    case "textMenu":
      if (d.options?.length)
        lines.push(`Opciones: ${d.options.map(o => o.keyword || o.id).join(" · ")}`);
      if (d.saveResponseTo) lines.push(`Guarda en: ${d.saveResponseTo}`);
      break;
    case "listButtons":
      const opts = (d.sections || []).flatMap(s => s.options || []);
      if (opts.length) lines.push(`Opciones: ${opts.map(o => o.title || o.id).join(" · ")}`);
      if (d.saveResponseTo) lines.push(`Guarda en: ${d.saveResponseTo}`);
      break;
    case "replyButtons":
      if (d.buttons?.length) lines.push(`Botones: ${d.buttons.map(b => b.label).join(" · ")}`);
      if (d.saveResponseTo) lines.push(`Guarda en: ${d.saveResponseTo}`);
      break;
    case "conditions":
      (d.conditions || []).forEach((c, i) => {
        lines.push(`  Condición ${i + 1}: ${c.variable} ${c.operator} "${c.value}"`);
      });
      if ((d.conditions || []).length > 1) lines.push(`  Lógica: ${d.logicOp}`);
      break;
    case "aiBot":
      if (d.outputVariable) lines.push(`Var salida: ${d.outputVariable}`);
      if (d.assistanceVar)  lines.push(`Var asistencia: ${d.assistanceVar}`);
      if (d.disconnectVar)  lines.push(`Var desconexión: ${d.disconnectVar}`);
      break;
    case "setField":
      lines.push(`${d.variable} ← "${d.value}"`);
      break;
    case "dbQuery":
      if (d.procedure) lines.push(`Procedimiento: ${d.procedure}`);
      if (d.outputVar) lines.push(`Var salida: ${d.outputVar}`);
      break;
    case "loopControl":
      lines.push(`Variable: ${d.variable}, Max ciclos: ${d.maxCycles}`);
      break;
    case "transferAgent":
      lines.push(`Cola: ${d.queue}, Prioridad: ${d.priority}`);
      break;
    case "acdSkill":
      if (d.skill) lines.push(`Habilidad: ${d.skill}`);
      if (d.skills?.length) lines.push(`Skills: ${d.skills.join(", ")}`);
      break;
    case "groqChat":
      if (d.model) lines.push(`Modelo: ${d.model}`);
      if (d.exitKeyword) lines.push(`Salida: "${d.exitKeyword}"`);
      break;
    case "calendar":
      if (d.paused) { lines.push("⏸ PAUSADO — siempre false"); break; }
      lines.push(`Días: ${(d.days||[]).map(n=>["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"][n]).join(", ")}`);
      lines.push(`Horario: ${d.timeStart||"00:00"} — ${d.timeEnd||"23:59"}`);
      if (d.holidays?.length) lines.push(`Feriados: ${d.holidays.length}`);
      break;
    case "flowControl":
      if (!(d.mappings?.length)) { lines.push("Sin mapeos configurados"); break; }
      (d.mappings || []).forEach(m => {
        if (m.traceName || m.targetVar)
          lines.push(`"${m.traceName || "?"}" → {{${m.targetVar || "?"}}}`);
      });
      break;
    default: break;
  }
  // Anotar imágenes embebidas o por URL
  for (const field of ["imageUrl", "mediaUrl", "headerMediaUrl"]) {
    if (!d[field]) continue;
    if (d[field].startsWith("data:")) {
      lines.push(`Imagen (${field}): [embebida en ZIP → images/]`);
    } else if (d[field].trim()) {
      lines.push(`Imagen (${field}): ${d[field].slice(0, 70)}`);
    }
  }
  return lines;
}

function inspectFlow(nodes, edges, vars, botName) {
  // --- BFS ---
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
  const edgesBySrc = {};
  for (const e of edges) {
    if (!edgesBySrc[e.src]) edgesBySrc[e.src] = [];
    edgesBySrc[e.src].push(e);
  }

  const startNode = nodes.find(n => n.type === "startingPoint");
  const visited = new Set();
  const queue = startNode ? [startNode] : [];
  const steps = [];
  const stepMap = {}; // nodeId → stepNumber

  while (queue.length) {
    const node = queue.shift();
    if (!node || visited.has(node.id)) continue;
    visited.add(node.id);

    const outEdges = edgesBySrc[node.id] || [];
    const outgoing = outEdges.map(e => {
      const tgt = nodeMap[e.tgt];
      return {
        edgeId: e.id,
        port: e.sp,
        portLabel: inspPortLabel(e.sp, node),
        targetNodeId: e.tgt,
        targetType: tgt?.type || "?",
        targetStep: null, // se rellena en segunda pasada
      };
    });

    const step = {
      step: steps.length + 1,
      nodeId: node.id,
      type: node.type,
      label: inspGetLabel(node),
      data: node.data,
      outgoing,
      reachedFrom: [],
    };
    stepMap[node.id] = step.step;
    steps.push(step);

    // Encolar destinos no visitados
    for (const e of outEdges) {
      const tgt = nodeMap[e.tgt];
      if (tgt && !visited.has(tgt.id)) queue.push(tgt);
    }
  }

  // Segunda pasada: resolver targetStep y reachedFrom
  for (const step of steps) {
    for (const out of step.outgoing) {
      out.targetStep = stepMap[out.targetNodeId] ?? null;
    }
  }
  for (const e of edges) {
    const tgtStep = steps.find(s => s.nodeId === e.tgt);
    const srcStep = steps.find(s => s.nodeId === e.src);
    if (tgtStep && srcStep) {
      tgtStep.reachedFrom.push({ sourceNodeId: e.src, sourceStep: srcStep.step, port: e.sp });
    }
  }

  const unreachable = nodes.filter(n => !visited.has(n.id));

  // --- Objeto completo para ZIP ---
  const full = {
    botName: botName || "Bot",
    generatedAt: new Date().toISOString(),
    stats: {
      totalNodes: nodes.length,
      reachableNodes: steps.length,
      unreachableNodes: unreachable.length,
      totalEdges: edges.length,
    },
    vars: (vars || []).map(v => ({ id: v.id, name: v.name, defaultValue: v.defaultValue })),
    traversal: steps,
    unreachable: unreachable.map(n => ({
      nodeId: n.id, type: n.type, label: inspGetLabel(n), data: n.data,
    })),
  };

  // --- Líneas para pantalla ---
  // Cada línea: { text, level }  level: "header"|"step"|"detail"|"edge"|"warn"
  const lines = [];
  const sep = "═".repeat(50);
  lines.push({ text: sep, level: "header" });
  lines.push({ text: `  Bot: "${botName}"   |   ${nodes.length} nodos   |   ${vars?.length || 0} vars`, level: "header" });
  if (vars?.length) lines.push({ text: `  Variables: ${vars.map(v => v.name).join(", ")}`, level: "header" });
  lines.push({ text: sep, level: "header" });

  for (const step of steps) {
    lines.push({ text: "", level: "header" });
    const label = step.label ? `: ${step.label}` : "";
    lines.push({ text: `PASO ${step.step} — ${step.type}${label}`, level: "step" });
    for (const dl of inspDetailLines(nodeMap[step.nodeId])) {
      lines.push({ text: `  ${dl}`, level: "detail" });
    }
    for (const out of step.outgoing) {
      const tgtLabel = out.targetStep ? `[PASO ${out.targetStep}]` : `[?]`;
      lines.push({
        text: `  → ${out.portLabel.padEnd(18)} ${tgtLabel} ${out.targetType}`,
        level: "edge",
      });
    }
  }

  if (unreachable.length) {
    lines.push({ text: "", level: "header" });
    lines.push({
      text: `⚠ NODOS NO ALCANZABLES (${unreachable.length}): ${unreachable.map(n => n.id.slice(0, 8)).join(", ")}`,
      level: "warn",
    });
  }

  return { lines, full };
}

// ============================================================
// COMPONENTE PRINCIPAL
// Campos de nodo que pueden contener data: URLs de imágenes (para export/import ZIP)
const IMAGE_FIELDS = ["imageUrl", "mediaUrl", "headerMediaUrl"];

// ============================================================
export default function WhatsAppBotDesigner({ onSendToEmulator, botId, initialData }) {
  // Inicializar desde los datos del bot recibidos del dashboard
  const initD = initialData || {};
  const [lang, setLang]     = useState(initD.lang || "es");
  const t = useT(lang);

  const [botName, setBotName] = useState(initD.botName || "Mi Bot WhatsApp");
  const [nodes, setNodes]     = useState(initD.nodes || [
    { id: "start_1", type: "startingPoint", x: 100, y: 280, data: {} },
  ]);
  const [edges, setEdges]     = useState(initD.edges || []);
  const [vars, setVars]       = useState(initD.vars || [
    { id: "v1", name: "nombre",   defaultValue: "", value: "" },
    { id: "v2", name: "telefono", defaultValue: "", value: "" },
  ]);
  const [bd, setBd]           = useState(initD.bd || { procedures: [] });
  const [aiConfig, setAiConfig] = useState(initD.aiConfig || { apiKey: "", model: "llama-3.1-8b-instant", systemPrompt: "", exitKeyword: "salir", maxTokens: 500 });

  // Indicador de estado de sincronización
  const [syncStatus, setSyncStatus] = useState("idle");
  const saveTimerRef = useRef(null);

  // Guardado con debounce 2s
  useEffect(() => {
    if (!botId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSyncStatus("saving");
    saveTimerRef.current = setTimeout(async () => {
      try {
        await saveFlow(botId, { botName, nodes, edges, vars, bd, aiConfig, lang }); // eslint-disable-line react-hooks/exhaustive-deps
        setSyncStatus("saved");
        setTimeout(() => setSyncStatus("idle"), 2000);
      } catch { setSyncStatus("error"); }
    }, 2000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [botName, nodes, edges, vars, bd, aiConfig, lang, botId]);

  // UI state
  const [selId, setSelId] = useState(null);
  const [selEdgeId, setSelEdgeId] = useState(null);   // edge seleccionado para inspección
  const [selEdgePos, setSelEdgePos] = useState(null); // posición canvas donde se clickeó el edge
  const [sideOpen, setSideOpen] = useState(true);
  const [propsOpen, setPropsOpen] = useState(false);
  const [varsOpen, setVarsOpen] = useState(false);
  const [bdOpen,   setBdOpen]   = useState(false);
  const [bdCollapsed, setBdCollapsed] = useState({}); // procId → bool
  const [expCat, setExpCat] = useState("messages");
  const [search, setSearch] = useState("");
  const [apiTest, setApiTest] = useState({ loading: false, status: null, body: null, error: null });

  // Canvas: zoom y pan
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(0.85);
  const [panning, setPanning] = useState(false);
  const [panSt, setPanSt] = useState({ x: 0, y: 0 });

  // Arrastrar nodos
  const [dragId, setDragId] = useState(null);
  const [dragOff, setDragOff] = useState({ x: 0, y: 0 });

  // Crear conexiones
  const [connFrom, setConnFrom] = useState(null);
  const [mpos, setMpos] = useState({ x: 0, y: 0 });

  // Notificaciones
  const [toast, setToast] = useState(null);

  // Inspector de flujo (modal post-import)
  const [flowInspOpen,  setFlowInspOpen]  = useState(false);
  const [flowInspLines, setFlowInspLines] = useState([]);
  const [flowInspData,  setFlowInspData]  = useState(null);

  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const zipFileRef = useRef(null);
  const cytFileRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0 });

  // Historia para deshacer / rehacer
  const MAX_HISTORY = 20;
  const histSnaps = useRef([]);
  const histPos = useRef(-1);
  const histRestoring = useRef(false);
  const draggingRef = useRef(false);   // true mientras se arrastra un nodo
  const nodesRef = useRef([]);         // espejo ref de nodes para captura al soltar
  const edgesRef = useRef([]);         // espejo ref de edges para captura al soltar
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const selNode = nodes.find(n => n.id === selId);

  // Mostrar notificación temporal
  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  // Convertir coordenadas de pantalla a canvas
  const s2c = useCallback((sx, sy) => {
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    return { x: (sx - r.left - offset.x) / scale, y: (sy - r.top - offset.y) / scale };
  }, [offset, scale]);

  // Obtener los puertos de salida efectivos de un nodo
  // (dinámicos para replyButtons y listButtons)
  const getOuts = useCallback((node) => {
    const def = NDEFS[node.type];
    if (!def) return [];
    if (node.type === "textMenu" && node.data.options) {
      return [...node.data.options.map(o => o.id), "invalid", "timeout"];
    }
    if (node.type === "replyButtons" && node.data.buttons) {
      return [...node.data.buttons.map(b => b.id), "any"];
    }
    if (node.type === "listButtons" && node.data.sections) {
      const all = node.data.sections.flatMap(s => s.options.map(o => o.id));
      return [...all, "any"];
    }
    return def.outs;
  }, []);

  // Obtener labels para puertos de salida
  const getOutLabels = useCallback((node) => {
    const labels = {};
    if (node.type === "replyButtons" && node.data.buttons) {
      node.data.buttons.forEach(b => labels[b.id] = b.label);
      labels.any = t("ui.anyOfAbove");
    } else if (node.type === "listButtons" && node.data.sections) {
      node.data.sections.flatMap(s => s.options).forEach(o => labels[o.id] = o.title);
      labels.any = t("ui.anyOfAbove");
    } else if (node.type === "textMenu" && node.data.options) {
      node.data.options.forEach(o => labels[o.id] = o.label || o.keyword || o.id);
      labels.invalid = "❌ Inválido";
      labels.timeout = "⏱ Timeout";
    } else if (node.type === "transferAgent") {
      labels.transferred = "✅ Transferido";
      labels.noOperators = "❌ Sin operadores";
    } else if (node.type === "loopControl") {
      labels.incomplete = "🔄 Ciclo incompleto";
      labels.complete = "✅ Ciclo completo";
    } else if (node.type === "aiBot") {
      labels.disconnect   = "📵 Desconexión";
      labels.ok           = "✅ Finaliza OK";
      labels.needsHuman   = "🧑‍💼 Requiere asistencia";
      labels.tagDetected  = "🏷 Detectó tag extra";
      labels.digitEnd     = "🔢 Dígito finalizador";
      labels.noResources  = "⚠️ Sin recursos";
      labels.error        = "❌ Error";
    } else if (node.type === "conditions") {
      labels["true"] = "✅ True";
      labels["false"] = "❌ False";
    } else if (node.type === "abTest") {
      labels.variantA = `A (${node.data.percentageA || 50}%)`;
      labels.variantB = `B (${100 - (node.data.percentageA || 50)}%)`;
    } else if (node.type === "keywordJump" || node.type === "keywordOptions") {
      labels.match = "✅ Match";
      labels.noMatch = "❌ No match";
    }
    return labels;
  }, [t]);

  // Alto extra del preview de imagen
  const imgPreviewH = (node) => {
    const url = node.data.imageUrl
      || (node.data.mediaType === "image" ? node.data.mediaUrl : null)
      || (node.data.headerType === "image" ? node.data.headerMediaUrl : null);
    return url ? 90 : 0;
  };

  // Alto del área de contenido del nodo — dinámico según cantidad de texto
  // NW=240, padding 12px c/lado → ~216px. font-size 10 Segoe UI ≈ 5.8px/char → ~37 chars/línea
  const CHARS_PER_LINE = 37;
  const LINE_H = 15;
  const BODY_PAD = 10;
  const MAX_LINES = 5;
  const bodyPreviewH = (node) => {
    if (node.type === "startingPoint") return 0;
    // Tomar el texto más relevante según el tipo de nodo
    const text = node.data.message || node.data.caption || node.data.question
      || node.data.body || node.data.goalName || node.data.templateName || "";
    if (!text) return LINE_H + BODY_PAD; // 1 línea vacía mínimo
    const lines = Math.min(MAX_LINES, Math.max(1, Math.ceil(text.length / CHARS_PER_LINE)));
    return lines * LINE_H + BODY_PAD;
  };

  // Calcular posición de un puerto para dibujar edges
  const portPos = useCallback((nodeId, portId, isInput) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return { x: 0, y: 0 };
    if (isInput) return { x: node.x, y: node.y + NH_HEAD / 2 };
    const outs = getOuts(node);
    const idx = outs.indexOf(portId);
    const extraH = imgPreviewH(node) + bodyPreviewH(node);
    const pFlipped = node.data?.portFlipped?.[portId];
    return { x: pFlipped ? node.x : node.x + NW, y: node.y + NH_HEAD + extraH + 14 + (idx >= 0 ? idx : 0) * PORT_SPC };
  }, [nodes, getOuts]);

  // Calcular altura del nodo basado en puertos
  const nodeH = useCallback((node) => {
    const outs = getOuts(node);
    const def = NDEFS[node.type];
    const extraH = imgPreviewH(node) + bodyPreviewH(node);
    let h = NH_HEAD + extraH + 16 + outs.length * PORT_SPC;
    if (def?.saves && node.data.saveResponseTo) h += 22;
    return Math.max(80, h);
  }, [getOuts]);

  // Auto-centra el viewport para que todos los nodos sean visibles
  const fitView = useCallback((nodeList) => {
    if (!nodeList?.length) return;
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;
    const { width: cw, height: ch } = canvasEl.getBoundingClientRect();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodeList) {
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + NW);
      maxY = Math.max(maxY, n.y + 120);
    }
    const pad = 60;
    const bw = maxX - minX + pad * 2;
    const bh = maxY - minY + pad * 2;
    const newScale = Math.max(0.03, Math.min(1.2, Math.min(cw / bw, ch / bh)));
    setScale(newScale);
    setOffset({
      x: cw / 2 - (minX + (maxX - minX) / 2) * newScale,
      y: ch / 2 - (minY + (maxY - minY) / 2) * newScale,
    });
  }, []);

  // ============================================================
  // HANDLERS DE CANVAS
  // ============================================================
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const r = canvasRef.current?.getBoundingClientRect();
    if (!r) return;
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    const d = e.deltaY > 0 ? 0.75 : 1.35;
    const ns = Math.min(Math.max(scale * d, 0.03), 3);
    const sr = ns / scale;
    setOffset({ x: mx - (mx - offset.x) * sr, y: my - (my - offset.y) * sr });
    setScale(ns);
  }, [scale, offset]);

  const onCanvasDown = useCallback((e) => {
    if (e.target === canvasRef.current || e.target.classList?.contains("cbg")) {
      setPanning(true);
      setPanSt({ x: e.clientX - offset.x, y: e.clientY - offset.y });
      setSelId(null);
      setPropsOpen(false);
    }
  }, [offset]);

  const onCanvasMove = useCallback((e) => {
    const cp = s2c(e.clientX, e.clientY);
    setMpos(cp);
    if (panning) {
      setOffset({ x: e.clientX - panSt.x, y: e.clientY - panSt.y });
    }
    if (dragId) {
      setNodes(p => p.map(n => n.id === dragId
        ? { ...n, x: cp.x - dragOff.x, y: cp.y - dragOff.y } : n));
    }
  }, [panning, panSt, dragId, dragOff, s2c]);

  const onCanvasUp = useCallback(() => {
    setPanning(false);
    if (draggingRef.current) {
      draggingRef.current = false;
      // Captura UN solo snapshot con la posición final del drag
      const snap = {
        nodes: JSON.parse(JSON.stringify(nodesRef.current)),
        edges: JSON.parse(JSON.stringify(edgesRef.current)),
      };
      histSnaps.current = histSnaps.current.slice(0, histPos.current + 1);
      histSnaps.current.push(snap);
      if (histSnaps.current.length > MAX_HISTORY) histSnaps.current.shift();
      histPos.current = histSnaps.current.length - 1;
      setCanUndo(histPos.current > 0);
      setCanRedo(false);
    }
    setDragId(null);
    setConnFrom(null);
  }, []);

  // Mantener refs sincronizados para captura manual al soltar drag
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);

  // Captura snapshot cada vez que nodes o edges cambian — SALVO durante drag
  useEffect(() => {
    if (histRestoring.current) { histRestoring.current = false; return; }
    if (draggingRef.current) return; // durante drag se captura solo al soltar
    const snap = { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) };
    histSnaps.current = histSnaps.current.slice(0, histPos.current + 1);
    histSnaps.current.push(snap);
    if (histSnaps.current.length > MAX_HISTORY) histSnaps.current.shift();
    histPos.current = histSnaps.current.length - 1;
    setCanUndo(histPos.current > 0);
    setCanRedo(false);
  }, [nodes, edges]);

  const undo = useCallback(() => {
    if (histPos.current <= 0) return;
    histPos.current -= 1;
    histRestoring.current = true;
    const { nodes: n, edges: e } = histSnaps.current[histPos.current];
    setNodes(JSON.parse(JSON.stringify(n)));
    setEdges(JSON.parse(JSON.stringify(e)));
    setCanUndo(histPos.current > 0);
    setCanRedo(true);
  }, []);

  const redo = useCallback(() => {
    if (histPos.current >= histSnaps.current.length - 1) return;
    histPos.current += 1;
    histRestoring.current = true;
    const { nodes: n, edges: e } = histSnaps.current[histPos.current];
    setNodes(JSON.parse(JSON.stringify(n)));
    setEdges(JSON.parse(JSON.stringify(e)));
    setCanUndo(true);
    setCanRedo(histPos.current < histSnaps.current.length - 1);
  }, []);

  // Atajos de teclado Ctrl+Z / Ctrl+Y
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if (e.ctrlKey && e.key === "z") { e.preventDefault(); undo(); }
      if (e.ctrlKey && e.key === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);

  // Prevenir scroll nativo en el canvas
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const h = e => e.preventDefault();
    el.addEventListener("wheel", h, { passive: false });
    return () => el.removeEventListener("wheel", h);
  }, []);

  // ============================================================
  // HANDLERS DE NODOS
  // ============================================================
  const onNodeDown = useCallback((e, id) => {
    e.stopPropagation();
    setConnFrom(null); // limpiar cualquier conexión pendiente al iniciar drag de nodo
    const cp = s2c(e.clientX, e.clientY);
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    const n = nodes.find(nd => nd.id === id);
    if (n) {
      setDragOff({ x: cp.x - n.x, y: cp.y - n.y });
      setDragId(id);
      draggingRef.current = true;
    }
    setSelId(id);
    setSelEdgeId(null);
  }, [nodes, s2c]);

  const onNodeUp = useCallback((e, id) => {
    const dx = e.clientX - dragStartPos.current.x;
    const dy = e.clientY - dragStartPos.current.y;
    const moved = Math.sqrt(dx * dx + dy * dy);
    if (moved < 5) {
      setSelId(id);
      setPropsOpen(true);
      setApiTest({ loading: false, status: null, body: null, error: null });
    }
  }, []);

  const deleteNode = useCallback((id) => {
    if (id === "start_1") return;
    setNodes(p => p.filter(n => n.id !== id));
    setEdges(p => p.filter(e => e.src !== id && e.tgt !== id));
    if (selId === id) { setSelId(null); setPropsOpen(false); }
  }, [selId]);

  const updNode = useCallback((id, data) => {
    setNodes(p => p.map(n => n.id === id ? { ...n, data: { ...n.data, ...data } } : n));
  }, []);

  // ============================================================
  // HANDLERS DE CONEXIONES
  // ============================================================
  const onOutDown = useCallback((e, nid, pid) => {
    e.stopPropagation();
    e.preventDefault();
    setConnFrom({ nid, pid });
  }, []);

  const onInUp = useCallback((e, nid) => {
    e.stopPropagation();
    // No crear conexión si estamos en medio de un drag de nodo
    if (connFrom && connFrom.nid !== nid && !draggingRef.current) {
      const exists = edges.some(ed => ed.src === connFrom.nid && ed.sp === connFrom.pid && ed.tgt === nid);
      if (!exists) {
        setEdges(p => [...p, { id: eid(), src: connFrom.nid, sp: connFrom.pid, tgt: nid, tp: "input" }]);
      }
    }
    setConnFrom(null);
  }, [connFrom, edges]);

  // ============================================================
  // HANDLERS DE VARIABLES
  // ============================================================
  const addVar = useCallback((name) => {
    const n = name || `var_${vars.length + 1}`;
    setVars(p => [...p, { id: `v${Date.now()}`, name: n, defaultValue: "", value: "" }]);
  }, [vars]);

  // ============================================================
  // BASE DE DATOS MOCK (BD)
  // ============================================================
  const syncBdFromNodes = useCallback(() => {
    const dbNodes = nodes.filter(n => n.type === "dbQuery" && n.data?.query);
    setBd(prev => {
      const existing = Object.fromEntries(prev.procedures.map(p => [p.name, p]));
      const synced = [];
      const seen = new Set();
      for (const n of dbNodes) {
        const name = n.data.query;
        if (seen.has(name)) continue;
        seen.add(name);
        if (existing[name]) {
          synced.push({ ...existing[name], outputFields: n.data.outputFields || [] });
        } else {
          synced.push({ id: uid(), name, outputFields: n.data.outputFields || [], rows: [{}] });
        }
      }
      // Mantener procedimientos huérfanos (nodo eliminado) sin borrar sus datos
      for (const p of prev.procedures) {
        if (!seen.has(p.name)) synced.push(p);
      }
      return { procedures: synced };
    });
  }, [nodes]);

  // ============================================================
  // IMPORT / EXPORT
  // ============================================================
  const doExport = useCallback(() => {
    const d = { name: botName, nodes, edges, vars, bd, aiConfig, lang, v: "2.0", at: new Date().toISOString() };
    const b = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(b);
    a.download = `${botName.replace(/\s+/g, "_")}_bot.json`; a.click();
    showToast("✅ Bot exportado correctamente");
  }, [botName, nodes, edges, vars, bd, lang, showToast]);

  const doImport = useCallback((e) => {
    const f = e.target.files[0]; if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => {
      try {
        const d = JSON.parse(ev.target.result);
        if (d.nodes) { setNodes(d.nodes); fitView(d.nodes); }
        if (d.edges) setEdges(d.edges);
        if (d.vars) setVars(d.vars);
        if (d.bd) setBd(d.bd);
        if (d.aiConfig) setAiConfig(d.aiConfig);
        if (d.name) setBotName(d.name);
        if (d.lang) setLang(d.lang);
        showToast("✅ Bot importado correctamente");
        // Inspeccionar flujo y abrir modal
        const insp = inspectFlow(d.nodes || [], d.edges || [], d.vars || [], d.name || botName);
        setFlowInspLines(insp.lines);
        setFlowInspData(insp.full);
        setFlowInspOpen(true);
      } catch (err) { showToast("❌ Error: " + err.message); }
    };
    r.readAsText(f);
    e.target.value = "";
  }, [showToast, fitView, botName, setFlowInspLines, setFlowInspData, setFlowInspOpen]);

  const doExportText = useCallback(() => {
    // Exportación simplificada del flujo para integración con sistemas externos
    const flow = nodes.map(n => ({
      id: n.id, tipo: n.type, nombre: t(`node.${n.type}`),
      config: n.data,
      salidas: edges.filter(e => e.src === n.id).map(e => ({ puerto: e.sp, destino: e.tgt })),
    }));
    const d = {
      bot: botName,
      variables: vars.map(v => ({ nombre: v.name, defecto: v.defaultValue })),
      flujo: flow,
    };
    const b = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(b);
    a.download = `${botName.replace(/\s+/g, "_")}_flow.json`; a.click();
    showToast("✅ Flujo exportado como texto");
  }, [nodes, edges, vars, botName, t, showToast]);

  // ============================================================
  // EXPORT / IMPORT ZIP (flujo + imágenes embebidas)
  // ============================================================

  const doExportZip = useCallback(async () => {
    const zip = new JSZip();
    const imgFolder = zip.folder("images");

    // Copia profunda de los nodos para no mutar el estado
    const nodesClean = JSON.parse(JSON.stringify(nodes));
    let imgIndex = 0;

    for (const node of nodesClean) {
      for (const field of IMAGE_FIELDS) {
        const val = node.data[field];
        if (val && val.startsWith("data:")) {
          // Extraer tipo MIME y datos base64
          const [meta, b64] = val.split(",");
          const mimeMatch = meta.match(/data:([^;]+);base64/);
          const mime = mimeMatch ? mimeMatch[1] : "image/png";
          const ext = mime.split("/")[1]?.split("+")[0] || "png";
          const fileName = `img_${imgIndex++}.${ext}`;

          // Guardar archivo en el ZIP
          imgFolder.file(fileName, b64, { base64: true });

          // Reemplazar data: URL por ruta relativa en el JSON
          node.data[field] = `images/${fileName}`;
        }
      }
    }

    const manifest = { name: botName, nodes: nodesClean, edges, vars, bd, aiConfig, lang, v: "2.0", at: new Date().toISOString() };
    zip.file("flow.json", JSON.stringify(manifest, null, 2));

    const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${botName.replace(/\s+/g, "_")}_bot.zip`;
    a.click();
    showToast("✅ ZIP exportado correctamente");
  }, [botName, nodes, edges, vars, bd, lang, showToast]);

  const doImportZip = useCallback((e) => {
    const f = e.target.files[0]; if (!f) return;
    e.target.value = "";

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const zip = await JSZip.loadAsync(ev.target.result);

        // Leer el manifest
        const jsonFile = zip.file("flow.json");
        if (!jsonFile) { showToast("❌ ZIP inválido: falta flow.json"); return; }
        const d = JSON.parse(await jsonFile.async("string"));

        // Reconstruir imágenes: paths relativos → data: URLs
        const nodesRestored = d.nodes || [];
        for (const node of nodesRestored) {
          for (const field of IMAGE_FIELDS) {
            const val = node.data[field];
            if (val && val.startsWith("images/")) {
              const imgFile = zip.file(val);
              if (imgFile) {
                const b64 = await imgFile.async("base64");
                // Inferir mime del nombre de archivo
                const ext = val.split(".").pop().toLowerCase();
                const mimeMap = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", mp4: "video/mp4", pdf: "application/pdf" };
                const mime = mimeMap[ext] || "application/octet-stream";
                node.data[field] = `data:${mime};base64,${b64}`;
              }
            }
          }
        }

        if (nodesRestored.length) { setNodes(nodesRestored); fitView(nodesRestored); }
        if (d.edges) setEdges(d.edges);
        if (d.vars) setVars(d.vars);
        if (d.bd) setBd(d.bd);
        if (d.aiConfig) setAiConfig(d.aiConfig);
        if (d.name) setBotName(d.name);
        if (d.lang) setLang(d.lang);
        showToast("✅ ZIP importado correctamente");
      } catch (err) { showToast("❌ Error al importar ZIP: " + err.message); }
    };
    reader.readAsArrayBuffer(f);
  }, [showToast, fitView]);

  // ============================================================
  // IMPORT BOT CYT (formato externo 4-JSON)
  // ============================================================
  const doImportCyt = useCallback((e) => {
    const files = Array.from(e.target.files);
    e.target.value = "";
    if (!files.length) return;

    // ── helpers ──────────────────────────────────────────────
    const fixEnc = (s) => {
      if (typeof s !== "string") return s;
      try { return decodeURIComponent(escape(s)); } catch (_) { return s; }
    };

    const NODE_TYPE_MAP = {
      NODO_OBTENER_CAUSA_CONEXION: "startingPoint",
      NODO_USSD: "textMenu",
      NODO_INTEGRACION_BOT: "aiBot",
      NODO_COMPARAR_VARIABLES: "conditions",
      NODO_CONSULTAR_BASE: "dbQuery",
      NODO_ASIGNA_VARIABLE: "setField",
      NODO_EMITIR_MENSAJE: "sendMessage",
      NODO_TRANSFIERE_ACD: "transferAgent",
      NODO_CONT_CICLO_REPETITIVO: "loopControl",
      NODO_LIBERAR_LINEA: "goodbye",
      NODO_PREFERENCIA_ATENCION: "acdSkill",
      NODO_CONFIGURADOR_BOTONES: "listButtons", // subtipo resuelto en buildData
      NODO_ETIQUETA_GENERAL_TITLE: null,
      NODO_ETIQUETA_COMENTARIO: null,
    };

    const PORT_MAP = {
      "Verdadero": "true", "Falso": "false",
      "Acceso válido": "success", "Error en consulta": "error",
      "No hay registros": "error", "Requiere asistencia humana": "needsHuman",
      "Finaliza OK": "ok", "Desconexión": "disconnect",
      "No hay operadores": "noOperators", "Fin de mensaje": "default",
      "Variable asignada": "default", "Texto": "default",
      "Ciclo completo": "complete", "Ciclo incomp.": "incomplete",
      "No Ingresó": "timeout", "Entrada Inválida": "invalid",
      "Entrada Válida": "tm_0",
      "Detectó tag extra": "tagDetected", "Marcó dígito finalizador": "digitEnd",
      "Error - Sin recursos": "noResources", "Error": "error",
      // NODO_CONFIGURADOR_BOTONES
      "Boton 1": "btn_0", "Boton 2": "btn_1", "Boton 3": "btn_2",
      "Lista 1": "opt_0", "Lista 2": "opt_1", "Lista 3": "opt_2",
      "Lista 4": "opt_3", "Lista 5": "opt_4", "Lista 6": "opt_5",
      "Lista 7": "opt_6", "Lista 8": "opt_7", "Lista 9": "opt_8",
      "Lista 10": "opt_9",
      "Salida por error": "any", "Salida por timeout": "any",
      // NODO_PREFERENCIA_ATENCION
      "Próximo nodo": "default",
    };

    const resolvePort = (rawLabel) => {
      const labels = rawLabel.split(",").map(s => s.trim());
      for (const lbl of labels) {
        if (PORT_MAP[lbl] !== undefined) return PORT_MAP[lbl];
        // "Lista N" → opt_{N-1}
        const mLista = lbl.match(/^Lista\s+(\d+)$/i);
        if (mLista) return `opt_${Number(mLista[1]) - 1}`;
        // "Boton N" → btn_{N-1}
        const mBoton = lbl.match(/^Bot[oó]n\s+(\d+)$/i);
        if (mBoton) return `btn_${Number(mBoton[1]) - 1}`;
        // numbered USSD option: "N - X"
        const m = lbl.match(/^(\d+)\s*-/);
        if (m) return `tm_${m[1]}`;
        // partial
        const lo = lbl.toLowerCase();
        if (lo.includes("desconex")) return "disconnect";
        if (lo.includes("finaliza")) return "ok";
        if (lo.includes("asistencia")) return "needsHuman";
        if (lo.includes("operador")) return "noOperators";
        if (lo.includes("registros")) return "error";
        if (lo.includes("válido") || lo.includes("valido")) return "success";
        if (lo.includes("verdad")) return "true";
        if (lo.includes("falso")) return "false";
        if (lo.includes("completo")) return "complete";
        if (lo.includes("incompl")) return "incomplete";
        if (lo.includes("inválid") || lo.includes("invalido")) return "invalid";
        if (lo.includes("ingres")) return "timeout";
        if (lo.includes("tag")) return "tagDetected";
        if (lo.includes("dígit") || lo.includes("digit")) return "digitEnd";
        if (lo.includes("recurso")) return "noResources";
        if (lo.includes("salida por")) return "any";
      }
      return "default";
    };

    const buildData = (extType, ent, visible) => {
      const e = ent || {};
      switch (extType) {
        case "NODO_INTEGRACION_BOT":
          return { assistantName: e.prop8 || "", welcomeThread: e.prop2 || "",
            outputVariable: e.prop16 || "", disconnectVar: e.prop3 || "",
            assistanceVar: e.prop4 || "", timeout: Number(e.prop0) || 5,
            maxTokens: Number(e.prop9) || 250, sleepTime: 750 };
        case "NODO_USSD":
          return { message: fixEnc(e.prop0 === "NULL" ? "" : e.prop0 || ""),
            options: [], retries: Number(e.prop3) || 3,
            timeoutSecs: Number(e.prop7) || 20,
            saveResponseTo: e.prop12 ? e.prop12.replace(/^%/, "{{").replace(/$/, "}}") : "" };
        case "NODO_COMPARAR_VARIABLES":
          return { variable: e.prop0 || "", value: e.prop2 || "",
            operator: e.prop1 || "6" };
        case "NODO_CONSULTAR_BASE":
          return { dsn: e.prop0 || "", procedure: e.prop2 || "",
            params: e.prop3 || "", outputVar: e.prop4 || "" };
        case "NODO_ASIGNA_VARIABLE":
          return { variable: e.prop0 || "", value: e.prop1 || "" };
        case "NODO_EMITIR_MENSAJE":
          return { message: fixEnc(e.prop0 || "") };
        case "NODO_TRANSFIERE_ACD":
          return { queue: e.prop1 || "", priority: e.prop0 === "1" ? "high" : "normal" };
        case "NODO_CONT_CICLO_REPETITIVO":
          return { variable: e.prop0 || "BUCLE", maxCycles: Number(e.prop1) || 3 };
        case "NODO_CONFIGURADOR_BOTONES": {
          let payload = null;
          try { payload = JSON.parse(e.prop1 || "{}"); } catch (_) {}
          const pdata = payload?.data || {};
          if (pdata.type === "quickReply") {
            const opts = pdata.quickReply?.options || [];
            return {
              headerType: "text", header: "", headerMediaUrl: "",
              body: fixEnc(pdata.quickReply?.content?.text || pdata.text || ""),
              footer: "",
              buttons: opts.slice(0, 3).map((o, i) => ({ id: `btn_${i}`, label: fixEnc(o.title || "") })),
              saveResponseTo: "",
            };
          } else {
            // list
            const lst = pdata.list || {};
            const allOpts = (lst.items || []).flatMap(item => item.options || []);
            const globalBtn = lst.globalButtons?.[0]?.title || "Ver opciones";
            return {
              header: fixEnc(lst.title || ""),
              body: fixEnc(lst.body || pdata.text || ""),
              footer: "",
              buttonText: fixEnc(globalBtn),
              sections: [{
                title: "",
                options: allOpts.map((o, i) => ({
                  id: `opt_${i}`,
                  title: fixEnc(o.title || ""),
                  desc: fixEnc(o.description || ""),
                })),
              }],
              saveResponseTo: "",
            };
          }
        }
        case "NODO_LIBERAR_LINEA":
          return { message: "" };
        default:
          return { label: visible || "" };
      }
    };

    // ── read all files ────────────────────────────────────────
    const readFile = (f) => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = (ev) => { try { res(JSON.parse(ev.target.result)); } catch (err) { rej(err); } };
      r.onerror = rej;
      r.readAsText(f);
    });

    const byName = {};
    for (const f of files) {
      const base = f.name.replace(/\.json$/i, "").toLowerCase();
      byName[base] = f;
    }

    // flexible key lookup
    const findFile = (key) => {
      for (const k of Object.keys(byName)) {
        if (k.includes(key)) return byName[k];
      }
      return null;
    };

    const fNodos = findFile("nodos");
    const fLinks = findFile("links");
    const fDatos = findFile("datos");
    const fMeta  = findFile("metadata") || findFile("meta");

    if (!fNodos || !fLinks || !fDatos) {
      showToast("❌ Seleccioná los 4 archivos JSON del bot externo (nodos, links, datosNodosLinks, metadataDiagrama)");
      return;
    }

    Promise.all([
      readFile(fNodos),
      readFile(fLinks),
      readFile(fDatos),
      fMeta ? readFile(fMeta) : Promise.resolve(null),
    ]).then(([nodosParsed, linksParsed, datosParsed, metaParsed]) => {
      const nodosArr = nodosParsed.nodos || [];
      const linksArr = linksParsed.links || [];
      const datosMap = datosParsed.datosNodosLinks || {};
      const camName  = metaParsed?.metadataDiagrama?.campania?.nombre || "Bot importado";

      // Build node id set for edge validation
      const nodeIds = new Set(nodosArr.map(n => n.id).filter(Boolean));

      // Infer textMenu options from links with "N - X" labels
      const menuOpts = {}; // nodeId → [{id, keyword, label}]
      for (const lk of linksArr) {
        const m = lk.text?.match(/^(\d+)\s*-\s*(\S+)/);
        if (m) {
          if (!menuOpts[lk.origin]) menuOpts[lk.origin] = [];
          menuOpts[lk.origin].push({ id: `tm_${m[1]}`, keyword: m[2], label: `Opción ${m[2]}` });
        }
      }

      // Translate nodes
      const outNodes = [];
      for (const n of nodosArr) {
        if (!n.id) continue;
        const mapped = NODE_TYPE_MAP[n.nodeType];
        if (mapped === null) continue; // skip decorative nodes
        const ent = datosMap[n.id]?.entrada;
        const data = buildData(n.nodeType, ent, datosMap[n.id]?.textoVisible || n.title);

        // Resolve dynamic type for NODO_CONFIGURADOR_BOTONES
        let type = mapped || "sendMessage";
        if (n.nodeType === "NODO_CONFIGURADOR_BOTONES") {
          try {
            const p = JSON.parse(ent?.prop1 || "{}");
            type = p?.data?.type === "quickReply" ? "replyButtons" : "listButtons";
          } catch (_) { type = "listButtons"; }
        }

        if (type === "textMenu" && menuOpts[n.id]) data.options = menuOpts[n.id];
        outNodes.push({ id: n.id, type, x: n.x * 4, y: n.y * 4, data });
      }

      // Translate edges (deduplicate by id)
      const seenEdges = new Set();
      const outEdges = [];
      for (const lk of linksArr) {
        if (!lk.origin || !lk.destination) continue;
        if (!nodeIds.has(lk.origin) || !nodeIds.has(lk.destination)) continue;
        if (seenEdges.has(lk.id)) continue;
        seenEdges.add(lk.id);
        // Split comma-separated labels → one edge per label (first only for port resolution)
        const sp = resolvePort(lk.text || "");
        outEdges.push({ id: `e_${lk.id}`, src: lk.origin, sp, tgt: lk.destination, tp: "input" });
      }

      setNodes(outNodes);
      fitView(outNodes);
      setEdges(outEdges);
      setVars([]);
      setBotName(camName);
      showToast(`✅ Bot CyT importado: ${outNodes.length} nodos, ${outEdges.length} conexiones`);
    }).catch(err => showToast("❌ Error importando Bot CyT: " + err.message));
  }, [showToast, fitView]);

  // ============================================================
  // EXPORT BOT → CyT (4 JSON + ZIP)
  // ============================================================
  const doExportCyt = useCallback(async () => {
    // Convierte {{VAR}} → %VAR
    const toVar = (s) => (typeof s === "string" ? s.replace(/\{\{(\w[\w.]*)\}\}/g, "%$1") : s ?? "");

    // Tipos internos → tipos CyT
    const CYT_TYPE = {
      startingPoint: "NODO_OBTENER_CAUSA_CONEXION",
      sendMessage:   "NODO_EMITIR_MENSAJE",
      media:         "NODO_EMITIR_MENSAJE",
      goodbye:       "NODO_LIBERAR_LINEA",
      textMenu:      "NODO_USSD",
      replyButtons:  "NODO_CONFIGURADOR_BOTONES",
      listButtons:   "NODO_CONFIGURADOR_BOTONES",
      conditions:    "NODO_COMPARAR_VARIABLES",
      setField:      "NODO_ASIGNA_VARIABLE",
      dbQuery:       "NODO_CONSULTAR_BASE",
      transferAgent: "NODO_TRANSFIERE_ACD",
      acdSkill:      "NODO_PREFERENCIA_ATENCION",
      loopControl:   "NODO_CONT_CICLO_REPETITIVO",
      aiBot:         "NODO_INTEGRACION_BOT",
      groqChat:      "NODO_INTEGRACION_BOT",
      askQuestion:   "NODO_USSD",
      askEmail:      "NODO_USSD",
      askNumber:     "NODO_USSD",
      askPhone:      "NODO_USSD",
      askDate:       "NODO_USSD",
      askFile:       "NODO_USSD",
      askAddress:    "NODO_USSD",
      askUrl:        "NODO_USSD",
    };

    // Puerto interno → etiqueta CyT
    const portLabel = (portId, node) => {
      if (portId === "default") {
        if (node.type === "setField")      return "Variable asignada";
        if (node.type === "acdSkill")      return "Próximo nodo";
        if (node.type === "startingPoint") return "Texto";
        return "Fin de mensaje";
      }
      const FIXED = {
        "true": "Verdadero", "false": "Falso",
        "success": "Acceso válido", "error": "Error en consulta",
        "ok": "Finaliza OK", "disconnect": "Desconexión",
        "noOperators": "No hay operadores", "needsHuman": "Requiere asistencia humana",
        "complete": "Ciclo completo", "incomplete": "Ciclo incomp.",
        "timeout": "No Ingresó", "invalid": "Entrada Inválida",
        "tm_0": "Entrada Válida",
        "tagDetected": "Detectó tag extra", "digitEnd": "Marcó dígito finalizador",
        "noResources": "Error - Sin recursos",
        "any": "Salida por error, Salida por timeout",
        "transferred": null, // transferAgent éxito — sin link saliente
      };
      if (portId in FIXED) return FIXED[portId];
      const mBtn = portId.match(/^btn_(\d+)$/);
      if (mBtn) return `Boton ${Number(mBtn[1]) + 1}`;
      const mOpt = portId.match(/^opt_(\d+)$/);
      if (mOpt) return `Boton ${Number(mOpt[1]) + 1}`;
      const mTm = portId.match(/^tm_(\d+)$/);
      if (mTm) {
        const idx = Number(mTm[1]);
        const opt = node.data?.options?.find(o => o.id === portId) || node.data?.options?.[idx];
        return `${idx} - ${opt?.keyword || String(idx)}`;
      }
      return null;
    };

    // Props de entrada por tipo de nodo
    const buildEntrada = (node) => {
      const d = node.data || {};
      const opMap = { equals: "6", not_equals: "2", greater: "3", less: "4", greater_eq: "5", less_eq: "1", contains: "8", is_empty: "10", is_not_empty: "11" };
      switch (node.type) {
        case "sendMessage":
          return { prop0: toVar(d.message || ""), prop1: "0", prop2: "7" };
        case "media":
          return { prop0: toVar(`${d.caption || ""} ${d.mediaUrl || ""}`.trim()), prop1: "0", prop2: "7" };
        case "goodbye":
          return {};
        case "textMenu":
        case "askQuestion": case "askEmail": case "askNumber": case "askPhone":
        case "askDate":     case "askFile":  case "askAddress": case "askUrl": {
          const msg = toVar(d.message || d.question || "");
          const sv  = d.saveResponseTo ? toVar(d.saveResponseTo) : "";
          return {
            prop0: msg, prop1: msg, prop2: msg,
            prop3: String(d.retries || 1),
            prop4: "999", prop5: "", prop6: "1",
            prop7: String(d.timeoutSecs || 600),
            prop8: "0", prop9: "2", prop10: "0", prop11: "0",
            prop12: sv,
          };
        }
        case "replyButtons": {
          const btns = (d.buttons || []).slice(0, 3);
          const body = toVar(d.body || "");
          const payload = {
            data: {
              type: "quickReply",
              text: body,
              quickReply: {
                providerId: "qr1",
                content: { type: "text", text: body },
                options: btns.map(b => ({ type: "text", title: toVar(b.label || "") })),
              },
            },
          };
          return { prop1: JSON.stringify(payload), prop2: body, prop3: body, prop4: body, prop5: "1", prop6: "600" };
        }
        case "listButtons": {
          const allOpts = (d.sections || []).flatMap(s => s.options || []);
          const body    = toVar(d.body || "");
          const header  = toVar(d.header || "");
          const btnTxt  = toVar(d.buttonText || "Ver opciones");
          const payload = {
            data: {
              type: "list",
              text: body,
              list: {
                providerId: "list1",
                title: header,
                body,
                globalButtons: [{ type: "text", title: btnTxt }],
                items: [{
                  title: "", subtitle: "",
                  options: allOpts.map(o => ({
                    title: toVar(o.title || ""),
                    description: toVar(o.desc || ""),
                    postbackText: toVar(o.title || ""),
                  })),
                }],
              },
            },
          };
          return { prop1: JSON.stringify(payload), prop2: body, prop3: body, prop4: body, prop5: "1", prop6: "600" };
        }
        case "conditions": {
          const cond = (d.conditions || [])[0] || {};
          return {
            prop0: toVar(cond.variable || ""),
            prop1: opMap[cond.operator] || "6",
            prop2: toVar(cond.value || ""),
          };
        }
        case "setField":
          return { prop0: toVar(d.variable || ""), prop1: toVar(d.value || "") };
        case "dbQuery":
          return {
            prop0: d.connectionString || d.dsn || "",
            prop1: "4",
            prop2: d.query || d.procedure || "",
            prop3: d.params || "",
            prop4: toVar(d.saveResponseTo || d.outputVar || ""),
            prop5: "", prop6: "", prop7: "1",
          };
        case "transferAgent":
          return { prop0: d.priority === "high" ? "1" : "0", prop1: "0" };
        case "acdSkill":
          return { prop0: "1", prop1: d.skill || "0000000000000001" };
        case "loopControl":
          return { prop0: `%${(d.variable || "BUCLE").replace(/^%/, "")}`, prop1: String(d.maxCycles || 3) };
        case "aiBot":
        case "groqChat":
          return {
            prop0: String(d.timeout || 5), prop1: "3",
            prop2: d.welcomeThread || "", prop3: toVar(d.disconnectVar || ""),
            prop4: toVar(d.assistanceVar || ""), prop5: "", prop6: "5",
            prop8: d.assistantName || "", prop9: String(d.maxTokens || 300),
            prop16: toVar(d.outputVariable || ""),
          };
        default:
          return {};
      }
    };

    // textoVisible: primer campo de texto significativo del nodo
    const getVisible = (node) => {
      const d = node.data || {};
      return d.textoVisible || d.label || d.message || d.question || d.procedure || d.queue || node.type;
    };

    // ── Layout jerárquico BFS con snake wrapping ─────────────
    const LEVEL_X  = 70;   // unidades CyT entre columnas
    const LEVEL_Y  = 45;   // unidades CyT entre filas dentro de una columna
    const MAX_COLS = 14;   // columnas antes de bajar y volver

    const exportableNodes = nodes.filter(n => CYT_TYPE[n.type]);
    const exportedIds = new Set(exportableNodes.map(n => n.id));
    const skippedTypes = new Set(nodes.filter(n => !CYT_TYPE[n.type]).map(n => n.type));

    // Grafo de adyacencia (solo nodos exportables)
    const adjOut = {};
    for (const node of exportableNodes) {
      adjOut[node.id] = edges
        .filter(e => e.src === node.id && exportedIds.has(e.tgt))
        .map(e => e.tgt);
    }

    // BFS desde startingPoint para asignar niveles
    const level = {};
    const startNode = exportableNodes.find(n => n.type === "startingPoint");
    const bfsQueue = startNode ? [startNode.id] : [];
    if (startNode) level[startNode.id] = 0;
    let bfsHead = 0;
    while (bfsHead < bfsQueue.length) {
      const id = bfsQueue[bfsHead++];
      for (const nextId of (adjOut[id] || [])) {
        if (!(nextId in level)) {
          level[nextId] = level[id] + 1;
          bfsQueue.push(nextId);
        }
      }
    }

    // Nodos desconectados: columnas extra al final
    const maxLv = Object.values(level).length ? Math.max(...Object.values(level)) : 0;
    let extraLv = maxLv + 2;
    for (const node of exportableNodes) {
      if (!(node.id in level)) level[node.id] = extraLv++;
    }

    // Agrupar por nivel, ordenar dentro de cada nivel por Y original del diseñador
    const byLevel = {};
    for (const [id, lv] of Object.entries(level)) {
      (byLevel[lv] = byLevel[lv] || []).push(id);
    }
    const nodeById = Object.fromEntries(nodes.map(n => [n.id, n]));
    for (const ids of Object.values(byLevel)) {
      ids.sort((a, b) => (nodeById[a]?.y ?? 0) - (nodeById[b]?.y ?? 0));
    }

    // Calcular posiciones finales — snake layout
    const maxNodesPerLevel = Math.max(...Object.values(byLevel).map(ids => ids.length), 1);
    const BAND_GAP = Math.max(180, maxNodesPerLevel * LEVEL_Y + 80);

    const layoutPos = {};
    for (const [lv, ids] of Object.entries(byLevel)) {
      const lvNum      = Number(lv);
      const band       = Math.floor(lvNum / MAX_COLS);
      const colInBand  = lvNum % MAX_COLS;
      // bandas pares: izquierda→derecha; impares: derecha→izquierda
      const effectiveCol = band % 2 === 0 ? colInBand : (MAX_COLS - 1 - colInBand);

      ids.forEach((id, row) => {
        layoutPos[id] = {
          x: 50 + effectiveCol * LEVEL_X,
          y: 50 + band * BAND_GAP + row * LEVEL_Y,
        };
      });
    }

    // ── Construir nodos ───────────────────────────────────────
    const nodosCyt = [];
    const datosCyt = {};
    let seq = 1;

    for (const node of exportableNodes) {
      const cytType = CYT_TYPE[node.type];
      const pos = layoutPos[node.id] || { x: 50, y: 50 };

      nodosCyt.push({
        id: node.id,
        nodeType: cytType,
        x: pos.x,
        y: pos.y,
        width: 24,
        height: 24,
        title: getVisible(node),
      });

      datosCyt[node.id] = {
        creadoEn: "NodosProxy",
        id: node.id,
        numeroNodoOriginal: seq++,
        textoVisible: getVisible(node),
        log: false,
        valido: true,
        entrada: buildEntrada(node),
      };
    }

    // ── Construir links (agrupar por src+dest para fusionar etiquetas) ──
    const linksCyt = [];
    for (const node of exportableNodes) {
      const byDest = {};
      for (const edge of edges) {
        if (edge.src !== node.id || !exportedIds.has(edge.tgt)) continue;
        (byDest[edge.tgt] = byDest[edge.tgt] || []).push(edge.sp);
      }

      for (const [destId, ports] of Object.entries(byDest)) {
        const labels = [...new Set(ports.map(p => portLabel(p, node)).filter(Boolean))];
        if (!labels.length) continue;

        const sp = layoutPos[node.id] || { x: 50, y: 50 };
        const dp = layoutPos[destId] || { x: sp.x + LEVEL_X, y: sp.y };

        linksCyt.push({
          id: crypto.randomUUID(),
          origin: node.id,
          destination: destId,
          text: labels.join(", "),
          puntos: [{ x: sp.x + 12, y: sp.y + 12 }, { x: dp.x, y: dp.y + 12 }],
        });
      }
    }

    // ── Generar entradas esUnLink para cada link ──────────────
    const buildSalidas = (node, cytType, linkText) => {
      const linkLabels = linkText.split(",").map(s => s.trim());

      if (cytType === "NODO_USSD") {
        if (node.type !== "textMenu") {
          return [{ checked: true, comments: "" }];
        }
        const opts = node.data?.options || [];
        const allLabels = [
          "Entrada Válida",
          ...opts.slice(1).map((o, i) => `${i + 1} - ${o.keyword || String(i + 1)}`),
          "Entrada Inválida",
          "No Ingresó",
        ];
        return allLabels.map(label => ({ checked: linkLabels.includes(label), comments: "" }));
      }

      if (cytType === "NODO_CONFIGURADOR_BOTONES") {
        if (node.type === "replyButtons") {
          const btns = (node.data?.buttons || []).slice(0, 3);
          return btns.map((_, i) => ({ checked: linkLabels.some(ll => ll === `Boton ${i + 1}`), comments: "" }));
        }
        const allOpts = (node.data?.sections || []).flatMap(s => s.options || []);
        return allOpts.slice(0, 10).map((_, i) => ({ checked: linkLabels.some(ll => ll === `Boton ${i + 1}`), comments: "" }));
      }

      const FIXED = {
        NODO_EMITIR_MENSAJE:         ["Fin de mensaje"],
        NODO_OBTENER_CAUSA_CONEXION: ["Texto"],
        NODO_ASIGNA_VARIABLE:        ["Variable asignada"],
        NODO_COMPARAR_VARIABLES:     ["Verdadero", "Falso"],
        NODO_CONSULTAR_BASE:         ["Acceso válido", "No hay registros", "Error en consulta"],
        NODO_TRANSFIERE_ACD:         ["No hay operadores"],
        NODO_PREFERENCIA_ATENCION:   ["Próximo nodo"],
        NODO_CONT_CICLO_REPETITIVO:  ["Ciclo incomp.", "Ciclo completo"],
        NODO_INTEGRACION_BOT:        ["Desconexión", "Finaliza OK", "Requiere asistencia humana", "Detectó tag extra", "Marcó dígito finalizador", "Error - Sin recursos", "Error"],
        NODO_LIBERAR_LINEA:          [],
      };
      const salidasDef = FIXED[cytType] || [];
      return salidasDef.map(label => ({ checked: linkLabels.includes(label), comments: "" }));
    };

    const nodesById = Object.fromEntries(nodes.map(n => [n.id, n]));
    for (const link of linksCyt) {
      const srcNode = nodesById[link.origin];
      if (!srcNode) continue;
      const cytType = CYT_TYPE[srcNode.type] || "";
      datosCyt[link.id] = {
        tipo: "esUnLink",
        id: link.id,
        originNodeType: cytType,
        salidas: buildSalidas(srcNode, cytType, link.text),
      };
    }

    // ── Empaquetar ZIP ────────────────────────────────────────
    const zip = new JSZip();
    zip.file("nodos.json",           JSON.stringify({ nodos: nodosCyt }, null, 2));
    zip.file("links.json",           JSON.stringify({ links: linksCyt }, null, 2));
    zip.file("datosNodosLinks.json", JSON.stringify({ datosNodosLinks: datosCyt }, null, 2));
    zip.file("metadataDiagrama.json", JSON.stringify({
      metadataDiagrama: {
        id: crypto.randomUUID(),
        version: 3,
        campania: { id: "1", nombre: botName },
        fechaGuardado:    new Date().toISOString(),
        fechaPublicacion: new Date().toISOString(),
      },
    }, null, 2));

    const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${botName.replace(/\s+/g, "_")}_cyt.zip`;
    a.click();

    const warn = skippedTypes.size ? ` (omitidos: ${[...skippedTypes].join(", ")})` : "";
    showToast(`✅ Exportado a CyT: ${nodosCyt.length} nodos, ${linksCyt.length} links${warn}`);
  }, [nodes, edges, botName, showToast]);

  // ============================================================
  // RENDER: Nodo SVG
  // ============================================================
  // Estado de conexión de un nodo: red | yellow | green
  const getNodeStatus = (node) => {
    const def = NDEFS[node.type];
    if (!def) return "red";
    const hasIncoming = def.ins.length === 0 || edges.some(e => e.tgt === node.id);
    const outs = getOuts(node);
    if (outs.length === 0) return hasIncoming ? "green" : "red";
    const connectedOuts = outs.filter(pid => edges.some(e => e.src === node.id && e.sp === pid));
    if (hasIncoming && connectedOuts.length === outs.length) return "green";
    if (!hasIncoming && connectedOuts.length === 0) return "red";
    return "yellow";
  };

  const STATUS_COLOR = { red: "#ef4444", yellow: "#f59e0b", green: "#22c55e" };

  const renderNode = (node) => {
    const def = NDEFS[node.type]; if (!def) return null;
    const isSel = selId === node.id;
    const selEdge = selEdgeId ? edges.find(e => e.id === selEdgeId) : null;
    const isEdgeNode = selEdge && (selEdge.src === node.id || selEdge.tgt === node.id);
    const name = node.data.label || t(`node.${node.type}`);
    const outs = getOuts(node);
    const labels = getOutLabels(node);
    const h = nodeH(node);
    const status = getNodeStatus(node);

    // Texto de preview: mostrar primer contenido relevante
    const preview = node.data.message || node.data.caption
      || node.data.question || node.data.body
      || node.data.goalName || node.data.templateName
      || t(`node.${node.type}.desc`);

    return (
      <g key={node.id} onMouseDown={e => onNodeDown(e, node.id)}
        onMouseUp={e => onNodeUp(e, node.id)}
        style={{ cursor: dragId === node.id ? "grabbing" : "grab" }}>
        {/* Sombra */}
        <rect x={node.x + 3} y={node.y + 3} width={NW} height={h} rx={10} fill="rgba(0,0,0,0.2)" />
        {/* Cuerpo — borde de color según estado de conexión */}
        <rect x={node.x} y={node.y} width={NW} height={h} rx={10}
          fill="#1e293b"
          stroke={isSel ? "#38bdf8" : isEdgeNode ? "#fbbf24" : STATUS_COLOR[status]}
          strokeWidth={isSel ? 2.5 : isEdgeNode ? 2.5 : 1.5}
          style={isEdgeNode ? { filter: "drop-shadow(0 0 6px #fbbf2466)" } : undefined} />
        {/* Header coloreado */}
        <path d={`M${node.x + 10},${node.y} h${NW - 20} a10,10 0 0 1 10,10 v${NH_HEAD - 10} h-${NW} v-${NH_HEAD - 10} a10,10 0 0 1 10,-10 z`}
          fill={def.color} />
        {/* Icono + nombre (centrado verticalmente en el header) */}
        <text x={node.x + 12} y={node.y + NH_HEAD / 2 + 5} fontSize={14} fill="white">{def.icon}</text>
        <text x={node.x + 34} y={node.y + NH_HEAD / 2 + 5} fontSize={12} fontWeight="700" fill="white"
          fontFamily="'Segoe UI',system-ui,sans-serif">{name}</text>

        {/* Área de contenido del nodo: mensaje, pregunta, etc. */}
        {node.type !== "startingPoint" && (() => {
          const bh = bodyPreviewH(node);
          const visibleLines = Math.floor((bh - BODY_PAD) / LINE_H);
          const maxChars = visibleLines * CHARS_PER_LINE;
          const truncated = preview.length > maxChars ? preview.slice(0, maxChars - 1) + "…" : preview;
          const lines = [];
          for (let i = 0; i < visibleLines; i++) {
            const chunk = truncated.slice(i * CHARS_PER_LINE, (i + 1) * CHARS_PER_LINE);
            if (!chunk) break;
            lines.push(chunk);
          }
          return (
            <g>
              <rect x={node.x} y={node.y + NH_HEAD} width={NW} height={bh} fill="#16213a" />
              {lines.map((line, i) => (
                <text key={i} x={node.x + 12} y={node.y + NH_HEAD + 13 + i * LINE_H}
                  fontSize={10} fill="#94a3b8" fontFamily="'Segoe UI',system-ui,sans-serif">
                  {line}
                </text>
              ))}
            </g>
          );
        })()}

        {/* Preview de imagen (después del área de contenido) */}
        {(() => {
          const imgUrl = node.data.imageUrl
            || (node.data.mediaType === "image" ? node.data.mediaUrl : null)
            || (node.data.headerType === "image" ? node.data.headerMediaUrl : null);
          if (!imgUrl) return null;
          const imgY = node.y + NH_HEAD + bodyPreviewH(node) + 6;
          return (
            <>
              <defs>
                <clipPath id={`clip-${node.id}`}>
                  <rect x={node.x + 8} y={imgY} width={NW - 16} height={78} rx={6} />
                </clipPath>
              </defs>
              <image href={imgUrl} x={node.x + 8} y={imgY} width={NW - 16} height={78}
                preserveAspectRatio="xMidYMid slice" clipPath={`url(#clip-${node.id})`} />
            </>
          );
        })()}

        {/* Puerto de ENTRADA (siempre izquierda) */}
        {def.ins.length > 0 && (() => {
          const inSel = selEdge?.tgt === node.id;
          const inColor = inSel ? "#fbbf24" : "#38bdf8";
          return (
            <g onMouseDown={e => e.stopPropagation()} onMouseUp={e => onInUp(e, node.id)} style={{ cursor: "crosshair" }}>
              <circle cx={node.x} cy={node.y + NH_HEAD / 2} r={PORT_R + 4} fill="transparent" />
              <circle cx={node.x} cy={node.y + NH_HEAD / 2} r={PORT_R} fill="#0f172a" stroke={inColor} strokeWidth={inSel ? 2.5 : 2}
                style={inSel ? { filter: "drop-shadow(0 0 5px #fbbf24)" } : undefined} />
              <circle cx={node.x} cy={node.y + NH_HEAD / 2} r={3} fill={inColor} />
            </g>
          );
        })()}

        {/* Puertos de SALIDA con labels */}
        {outs.map((pid, i) => {
          const extraH = imgPreviewH(node) + bodyPreviewH(node);
          const py = node.y + NH_HEAD + extraH + 14 + i * PORT_SPC;
          const lbl = labels[pid] || "";
          const isAny = pid === "any";
          const pFlipped = node.data?.portFlipped?.[pid];
          const pcx = pFlipped ? node.x : node.x + NW;
          const textX = pFlipped ? node.x + NW - 18 : node.x + 18;
          return (
            <g key={pid}>
              <rect x={node.x + 10} y={py - 10} width={NW - 20} height={24} rx={5} fill="#1e293b" />
              <rect x={node.x + 10} y={py - 10} width={NW - 20} height={24} rx={5}
                fill={isAny ? "rgba(148,163,184,0.15)" : `${def.color}25`}
                stroke={isAny ? "#475569" : `${def.color}60`} strokeWidth={0.5}
                strokeDasharray={isAny ? "3,2" : "none"} />
              <text x={textX} y={py + 4} fontSize={10}
                textAnchor={pFlipped ? "end" : "start"}
                fill={isAny ? "#94a3b8" : "#cbd5e1"} fontFamily="'Segoe UI',system-ui,sans-serif">
                {(() => {
                  const raw = lbl || pid;
                  if (raw === "default") return "→ Continuar";
                  return raw.length > 26 ? raw.slice(0, 26) + "…" : raw;
                })()}
              </text>
              {/* Botón flip individual del puerto (solo cuando nodo seleccionado) */}
              {isSel && (
                <g onMouseDown={e => e.stopPropagation()} onClick={e => {
                  e.stopPropagation();
                  setNodes(p => p.map(n => n.id === node.id ? {
                    ...n, data: {
                      ...n.data,
                      portFlipped: { ...n.data?.portFlipped, [pid]: !n.data?.portFlipped?.[pid] }
                    }
                  } : n));
                }} style={{ cursor: "pointer" }}>
                  <circle cx={pFlipped ? node.x + NW - 6 : node.x + 6} cy={py} r={7} fill="#6366f1" opacity={0.85} />
                  <text x={pFlipped ? node.x + NW - 6 : node.x + 6} y={py + 4} fontSize={9}
                    textAnchor="middle" fill="white">{pFlipped ? "▶" : "◀"}</text>
                </g>
              )}
              {/* Círculo del puerto */}
              {(() => {
                const outSel = selEdge?.src === node.id && selEdge?.sp === pid;
                const outColor = outSel ? "#fbbf24" : def.color;
                return (
                  <g onMouseDown={e => onOutDown(e, node.id, pid)} style={{ cursor: "crosshair" }}>
                    <circle cx={pcx} cy={py} r={PORT_R + 4} fill="transparent" />
                    <circle cx={pcx} cy={py} r={PORT_R} fill="#0f172a" stroke={outColor} strokeWidth={outSel ? 2.5 : 2}
                      style={outSel ? { filter: "drop-shadow(0 0 5px #fbbf24)" } : undefined} />
                    <circle cx={pcx} cy={py} r={3} fill={outColor} />
                  </g>
                );
              })()}
            </g>
          );
        })}

        {/* Indicador de variable guardada */}
        {def.saves && (node.data.saveResponseTo || node.data.resultVariable || node.data.variable) && (
          <g>
            <rect x={node.x + 10} y={node.y + h - 22} width={NW - 20} height={16} rx={3}
              fill="rgba(56,189,248,0.12)" />
            <text x={node.x + 16} y={node.y + h - 11} fontSize={9} fill="#38bdf8" fontFamily="monospace">
              💾 {`{{${node.data.saveResponseTo || node.data.resultVariable || node.data.variable}}}`}
            </text>
          </g>
        )}

        {/* Botón eliminar (solo seleccionado y no es start) */}
        {isSel && node.type !== "startingPoint" && (
          <g onClick={e => { e.stopPropagation(); deleteNode(node.id); }} style={{ cursor: "pointer" }}>
            <circle cx={node.x + NW - 8} cy={node.y - 8} r={11} fill="#ef4444" />
            <text x={node.x + NW - 8} y={node.y - 4} fontSize={13} fill="white"
              textAnchor="middle" fontWeight="bold">×</text>
          </g>
        )}
      </g>
    );
  };

  // ============================================================
  // RENDER: Edge (conexión con curva Bezier)
  // ============================================================
  const renderEdge = useCallback((edge) => {
    const from = portPos(edge.src, edge.sp, false);
    const to = portPos(edge.tgt, edge.tp, true);
    const srcNode = nodes.find(n => n.id === edge.src);
    const tgtNode = nodes.find(n => n.id === edge.tgt);
    const srcPortFlipped = srcNode?.data?.portFlipped?.[edge.sp];
    const tgtPortFlipped = tgtNode?.data?.portFlipped?.[edge.tp];
    const dx = Math.max(Math.abs(to.x - from.x) * 0.4, 50);
    // Punto de control: sale desde el lado donde está el puerto
    const fromDx = srcPortFlipped ? -dx : dx;
    const toDx   = tgtPortFlipped ?  dx : -dx;
    const path = `M${from.x},${from.y} C${from.x + fromDx},${from.y} ${to.x + toDx},${to.y} ${to.x},${to.y}`;
    const color = NDEFS[srcNode?.type]?.color || "#38bdf8";
    const isSel = selEdgeId === edge.id;

    const selColor = "#fbbf24"; // amarillo cuando está seleccionado

    return (
      <g key={edge.id}>
        <path d={path} fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth={isSel ? 6 : 4} />
        <path d={path} fill="none"
          stroke={isSel ? selColor : color}
          strokeWidth={isSel ? 3 : 2}
          strokeLinecap="round"
          style={{ filter: isSel ? `drop-shadow(0 0 6px ${selColor})` : `drop-shadow(0 0 3px ${color}30)` }} />
        {/* Zona clickeable — selecciona el edge */}
        <path d={path} fill="none" stroke="transparent" strokeWidth={14} style={{ cursor: "pointer" }}
          onClick={e => {
            e.stopPropagation();
            const pos = s2c(e.clientX, e.clientY);
            setSelEdgeId(id => id === edge.id ? null : edge.id);
            setSelEdgePos(pos);
            setSelId(null);
            setPropsOpen(false);
          }} />
        {/* Animación de flujo — oculta si seleccionado */}
        {!isSel && (
          <circle r={3} fill={color} opacity={0.8}>
            <animateMotion dur="2.5s" repeatCount="indefinite" path={path} />
          </circle>
        )}
        {/* Botón eliminar — solo visible cuando está seleccionado */}
        {isSel && selEdgePos && (
          <g
            onClick={e => { e.stopPropagation(); setEdges(p => p.filter(ed => ed.id !== edge.id)); setSelEdgeId(null); setSelEdgePos(null); }}
            style={{ cursor: "pointer" }}
          >
            <circle cx={selEdgePos.x} cy={selEdgePos.y} r={11} fill="#1e293b" stroke={selColor} strokeWidth={1.5} />
            <text x={selEdgePos.x} y={selEdgePos.y + 5} textAnchor="middle" fontSize={13} fill={selColor} style={{ userSelect: "none" }}>🗑</text>
          </g>
        )}
      </g>
    );
  }, [portPos, nodes, selEdgeId, selEdgePos, s2c]);

  // ── Tester de apiCall ──────────────────────────────────────
  const runApiTest = async (d, mode = "proxy") => {
    setApiTest({ loading: true, status: null, body: null, error: null, mode });
    const resolve = str => (str || "").replace(/\{\{(\w+)\}\}/g, (_, n) => {
      const v = vars.find(x => x.name === n);
      return v ? (v.defaultValue || v.value || `{{${n}}}`) : `{{${n}}}`;
    });
    try {
      const url = resolve(d.url || "");
      if (!url) throw new Error("URL vacía");
      const method = d.method || "GET";
      const headers = {};
      try { Object.assign(headers, JSON.parse(resolve(d.headers || "{}"))); } catch {}
      if (d.authType === "bearer" && d.authValue)
        headers["Authorization"] = `Bearer ${resolve(d.authValue)}`;
      else if (d.authType === "basic" && d.authValue)
        headers["Authorization"] = `Basic ${btoa(resolve(d.authValue))}`;
      else if (d.authType === "apikey" && d.authValue) {
        const [hName, ...hVal] = resolve(d.authValue).split(":");
        if (hName) headers[hName.trim()] = hVal.join(":").trim();
      }
      const requestBody = ["POST", "PUT", "PATCH"].includes(method) && d.body
        ? resolve(d.body) : undefined;

      let resultStatus, resultOk, resultBody;

      const proxyUrl = mode === "proxy" ? "/api/proxy" : "http://localhost:7071/api/proxy";
      const proxyResp = await fetch(proxyUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, method, headers, requestBody, timeout: Number(d.timeout) || 10 }),
      });
      const rawText = await proxyResp.text();
      let data;
      try { data = JSON.parse(rawText); }
      catch { throw new Error(rawText.substring(0, 300)); }
      if (data.error && !data.status) throw new Error(data.error);
      resultStatus = data.status;
      resultOk = data.ok;
      resultBody = data.body ?? "";

      let body = resultBody;
      try { body = JSON.stringify(JSON.parse(resultBody), null, 2); } catch {}
      setApiTest({ loading: false, status: resultStatus, body, error: null, mode });
    } catch (err) {
      setApiTest({ loading: false, status: null, body: null, error: err.message, mode });
    }
  };

  // ============================================================
  // RENDER: Panel de Propiedades del nodo seleccionado
  // ============================================================
  const renderProps = () => {
    if (!selNode || !propsOpen) return null;
    const def = NDEFS[selNode.type]; if (!def) return null;
    const d = selNode.data;
    const upd = (k, v) => updNode(selNode.id, { [k]: v });

    // Función para crear una nueva variable rápidamente
    const quickAddVar = (name) => addVar(name);

    // Propiedades comunes de "pregunta" (se reutiliza en todos los askXxx)
    const questionProps = () => (
      <>
        <TInput label={t("ui.message")} value={d.question} onChange={v => upd("question", v)}
          multi ph="Ej: ¿Cuál es su {{nombre}}?" />
        {d.errorMessage !== undefined && (
          <TInput label={t("ui.errorMsg")} value={d.errorMessage} onChange={v => upd("errorMessage", v)} />
        )}
        <VarSel label={t("ui.saveVar")} value={d.saveResponseTo}
          onChange={v => upd("saveResponseTo", v)} vars={vars} onAddVar={quickAddVar} t={t} />
      </>
    );

    let content;
    switch (selNode.type) {
      case "startingPoint":
        content = (
          <>
            <TInput label="Nombre de campaña" value={d.campaignName}
              onChange={v => upd("campaignName", v)} ph="Ej: Campaña Verano 2025" />

            <div style={{ marginBottom: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <label style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Intervalos activos
                </label>
                <button
                  onClick={() => upd("activeIntervals", [
                    ...(d.activeIntervals || []),
                    { id: `iv_${Date.now()}`, dateFrom: "", dateTo: "", timeFrom: "00:00", timeTo: "23:59" }
                  ])}
                  style={{
                    padding: "2px 8px", background: "#1e40af", border: "none",
                    borderRadius: 4, color: "#fff", fontSize: 11, cursor: "pointer",
                  }}
                >+ Agregar</button>
              </div>

              {!(d.activeIntervals?.length) && (
                <p style={{ fontSize: 11, color: "#475569", margin: 0 }}>
                  Sin restricción de horario — siempre activa
                </p>
              )}

              {(d.activeIntervals || []).map((iv, i) => (
                <div key={iv.id} style={{
                  background: "#0f172a", border: "1px solid #1e293b",
                  borderRadius: 8, padding: 10, marginBottom: 8,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 10, color: "#64748b", fontWeight: 700 }}>Intervalo {i + 1}</span>
                    <button
                      onClick={() => upd("activeIntervals", d.activeIntervals.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14, lineHeight: 1 }}
                    >×</button>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b", display: "block", marginBottom: 2 }}>Desde (fecha)</label>
                      <input type="date" value={iv.dateFrom}
                        onChange={e => {
                          const ni = [...d.activeIntervals];
                          ni[i] = { ...ni[i], dateFrom: e.target.value };
                          upd("activeIntervals", ni);
                        }}
                        style={{ ...inputSt, padding: "5px 7px", fontSize: 11 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b", display: "block", marginBottom: 2 }}>Hasta (fecha)</label>
                      <input type="date" value={iv.dateTo}
                        onChange={e => {
                          const ni = [...d.activeIntervals];
                          ni[i] = { ...ni[i], dateTo: e.target.value };
                          upd("activeIntervals", ni);
                        }}
                        style={{ ...inputSt, padding: "5px 7px", fontSize: 11 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b", display: "block", marginBottom: 2 }}>Hora inicio</label>
                      <input type="time" value={iv.timeFrom}
                        onChange={e => {
                          const ni = [...d.activeIntervals];
                          ni[i] = { ...ni[i], timeFrom: e.target.value };
                          upd("activeIntervals", ni);
                        }}
                        style={{ ...inputSt, padding: "5px 7px", fontSize: 11 }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 9, color: "#64748b", display: "block", marginBottom: 2 }}>Hora fin</label>
                      <input type="time" value={iv.timeTo}
                        onChange={e => {
                          const ni = [...d.activeIntervals];
                          ni[i] = { ...ni[i], timeTo: e.target.value };
                          upd("activeIntervals", ni);
                        }}
                        style={{ ...inputSt, padding: "5px 7px", fontSize: 11 }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Variables globales */}
            <div style={{ borderTop: "1px solid #1e293b", paddingTop: 12, marginTop: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Variables globales
                </label>
                <button
                  onClick={() => {
                    const n = `var_${vars.length + 1}`;
                    addVar(n);
                  }}
                  style={{ padding: "2px 8px", background: "#1e40af", border: "none", borderRadius: 4, color: "#fff", fontSize: 11, cursor: "pointer" }}
                >+ Nueva</button>
              </div>
              <div style={{ fontSize: 10, color: "#475569", marginBottom: 8 }}>
                Valor inicial para simulación (no afecta el flujo)
              </div>
              {vars.map(v => (
                <div key={v.id} style={{ display: "flex", gap: 4, marginBottom: 6, alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: 3 }}>
                    <input
                      value={v.name}
                      onChange={e => setVars(p => p.map(vr => vr.id === v.id ? { ...vr, name: e.target.value } : vr))}
                      style={{ ...inputSt, color: "#38bdf8", fontFamily: "monospace", fontSize: 11 }}
                      placeholder="nombre"
                    />
                    <input
                      value={v.defaultValue}
                      onChange={e => setVars(p => p.map(vr => vr.id === v.id ? { ...vr, defaultValue: e.target.value } : vr))}
                      style={{ ...inputSt, fontSize: 11 }}
                      placeholder="valor inicial..."
                    />
                  </div>
                  <button
                    onClick={() => setVars(p => p.filter(vr => vr.id !== v.id))}
                    style={{ padding: "0 7px", background: "none", border: "1px solid #334155", borderRadius: 5, color: "#ef4444", cursor: "pointer", fontSize: 14, alignSelf: "stretch" }}
                  >×</button>
                </div>
              ))}
            </div>
          </>
        );
        break;
      case "sendMessage":
        content = (
          <>
            <TInput label={t("ui.message")} value={d.message} onChange={v => upd("message", v)}
              multi ph={t("ui.varHint")} maxLength={1024} />
            <VarInsert value={d.message} onChange={v => upd("message", v)} vars={vars} />
            <MediaUpload label={`${t("ui.mediaUrl")} (imagen)`} value={d.imageUrl}
              onChange={v => upd("imageUrl", v)} accept="image/*" />
          </>
        );
        break;
      case "media":
        content = (
          <>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>
                {t("ui.mediaType")}
              </label>
              <select value={d.mediaType} onChange={e => upd("mediaType", e.target.value)} style={inputSt}>
                <option value="image">🖼️ Imagen</option>
                <option value="video">🎬 Video</option>
                <option value="document">📄 Documento</option>
                <option value="audio">🔊 Audio</option>
              </select>
            </div>
            <MediaUpload
              label={t("ui.mediaUrl")}
              value={d.mediaUrl}
              onChange={v => upd("mediaUrl", v)}
              accept={d.mediaType === "image" ? "image/*" : d.mediaType === "video" ? "video/*" : d.mediaType === "audio" ? "audio/*" : "*/*"}
            />
            <TInput label="Caption" value={d.caption} onChange={v => upd("caption", v)} multi maxLength={1024} />
            <VarInsert value={d.caption} onChange={v => upd("caption", v)} vars={vars} />
          </>
        );
        break;
      case "goodbye":
        content = <TInput label={t("ui.message")} value={d.message} onChange={v => upd("message", v)} multi maxLength={1024} />;
        break;
      case "replyButtons":
        content = (
          <>
            {/* Tipo de encabezado */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>
                Tipo de encabezado
              </label>
              <select value={d.headerType || "text"} onChange={e => upd("headerType", e.target.value)} style={inputSt}>
                <option value="text">📝 Texto</option>
                <option value="image">🖼️ Imagen</option>
                <option value="video">🎬 Video</option>
                <option value="document">📄 Documento</option>
              </select>
            </div>
            {(!d.headerType || d.headerType === "text") ? (
              <>
                <TInput label={t("ui.header")} value={d.header} onChange={v => upd("header", v)} maxLength={60} />
                <VarInsert value={d.header} onChange={v => upd("header", v)} vars={vars} />
              </>
            ) : (
              <MediaUpload
                label={`${t("ui.mediaUrl")} (${d.headerType})`}
                value={d.headerMediaUrl}
                onChange={v => upd("headerMediaUrl", v)}
                accept={d.headerType === "image" ? "image/*" : d.headerType === "video" ? "video/*" : "*/*"}
              />
            )}
            <TInput label={t("ui.body")} value={d.body} onChange={v => upd("body", v)} multi ph={t("ui.varHint")} maxLength={1024} />
            <VarInsert value={d.body} onChange={v => upd("body", v)} vars={vars} />
            <TInput label={t("ui.footer")} value={d.footer} onChange={v => upd("footer", v)} maxLength={60} />
            <VarInsert value={d.footer} onChange={v => upd("footer", v)} vars={vars} />
            <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 6 }}>
              {t("ui.buttons")} (máx 3 · 20 chars c/u)
            </label>
            {(d.buttons || []).map((btn, i) => (
              <div key={btn.id} style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                <input value={btn.label} maxLength={20} onChange={e => {
                  const nb = [...d.buttons]; nb[i] = { ...nb[i], label: e.target.value };
                  upd("buttons", nb);
                }} style={{ ...inputSt, flex: 1 }} />
                {d.buttons.length > 1 && (
                  <button onClick={() => upd("buttons", d.buttons.filter((_, j) => j !== i))}
                    style={delBtnSt}>×</button>
                )}
              </div>
            ))}
            {(d.buttons || []).length < 3 && (
              <button onClick={() => upd("buttons", [...d.buttons, { id: `btn_${d.buttons.length}`, label: `Opción ${d.buttons.length + 1}` }])}
                style={addBtnSt}>+ {t("ui.addButton")}</button>
            )}
            <VarSel value={d.saveResponseTo} onChange={v => upd("saveResponseTo", v)}
              vars={vars} onAddVar={quickAddVar} t={t} />
          </>
        );
        break;
      case "listButtons":
        content = (
          <>
            <TInput label={t("ui.header")} value={d.header} onChange={v => upd("header", v)} maxLength={60} />
            <VarInsert value={d.header} onChange={v => upd("header", v)} vars={vars} />
            <TInput label={t("ui.body")} value={d.body} onChange={v => upd("body", v)} multi maxLength={1024} />
            <VarInsert value={d.body} onChange={v => upd("body", v)} vars={vars} />
            <TInput label={t("ui.footer")} value={d.footer} onChange={v => upd("footer", v)} maxLength={60} />
            <VarInsert value={d.footer} onChange={v => upd("footer", v)} vars={vars} />
            {(d.sections || []).map((sec, si) => (
              <div key={si} style={{ border: "1px solid #334155", borderRadius: 6, padding: 8, marginBottom: 8 }}>
                <TInput label={`${t("ui.section")} ${si + 1}`} value={sec.title} maxLength={24}
                  onChange={v => { const ns = [...d.sections]; ns[si] = { ...ns[si], title: v }; upd("sections", ns); }} />
                {sec.options.map((opt, oi) => (
                  <div key={opt.id} style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                    <input value={opt.title} maxLength={24} onChange={e => {
                      const ns = JSON.parse(JSON.stringify(d.sections));
                      ns[si].options[oi].title = e.target.value;
                      upd("sections", ns);
                    }} placeholder="Título (máx 24)" style={{ ...inputSt, flex: 1 }} />
                    {sec.options.length > 1 && (
                      <button onClick={() => {
                        const ns = JSON.parse(JSON.stringify(d.sections));
                        ns[si].options.splice(oi, 1);
                        upd("sections", ns);
                      }} style={delBtnSt}>×</button>
                    )}
                  </div>
                ))}
                {sec.options.length < 10 && (
                  <button onClick={() => {
                    const ns = JSON.parse(JSON.stringify(d.sections));
                    ns[si].options.push({ id: `opt_${Date.now()}`, title: `Opción ${sec.options.length + 1}`, desc: "" });
                    upd("sections", ns);
                  }} style={addBtnSt}>+ {t("ui.addOption")}</button>
                )}
              </div>
            ))}
            <VarSel value={d.saveResponseTo} onChange={v => upd("saveResponseTo", v)}
              vars={vars} onAddVar={quickAddVar} t={t} />
          </>
        );
        break;
      case "keywordOptions":
        content = (
          <>
            {(d.keywords || []).map((kw, i) => (
              <div key={i} style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                <input value={kw.keyword} onChange={e => {
                  const nk = [...d.keywords]; nk[i] = { ...nk[i], keyword: e.target.value };
                  upd("keywords", nk);
                }} placeholder={t("ui.keyword")} style={{ ...inputSt, flex: 1 }} />
                <button onClick={() => upd("keywords", d.keywords.filter((_, j) => j !== i))}
                  style={delBtnSt}>×</button>
              </div>
            ))}
            <button onClick={() => upd("keywords", [...(d.keywords || []), { keyword: "", response: "" }])}
              style={addBtnSt}>+ {t("ui.addKeyword")}</button>
            <VarSel value={d.saveResponseTo} onChange={v => upd("saveResponseTo", v)}
              vars={vars} onAddVar={quickAddVar} t={t} />
          </>
        );
        break;
      case "sendTemplate":
        content = (
          <>
            <TInput label={t("ui.templateName")} value={d.templateName} onChange={v => upd("templateName", v)} />
            <TInput label={t("ui.language")} value={d.language} onChange={v => upd("language", v)} ph="es" />
          </>
        );
        break;
      case "contactSubscribe":
        content = <TInput label="Tags (separados por coma)" value={(d.tags || []).join(", ")}
          onChange={v => upd("tags", v.split(",").map(s => s.trim()))} />;
        break;
      case "askQuestion": case "askEmail":
      case "askDate": case "askFile": case "askAddress": case "askUrl":
        content = questionProps();
        break;
      case "askNumber":
        content = (
          <>
            {questionProps()}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 4 }}>
              <div>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>
                  Valor mínimo
                </label>
                <input type="number" value={d.min ?? ""} onChange={e => upd("min", e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="Sin límite" style={{ ...inputSt, padding: "5px 7px", fontSize: 11 }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>
                  Valor máximo
                </label>
                <input type="number" value={d.max ?? ""} onChange={e => upd("max", e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="Sin límite" style={{ ...inputSt, padding: "5px 7px", fontSize: 11 }} />
              </div>
            </div>
            {(d.min !== null && d.min !== undefined && d.min !== "") || (d.max !== null && d.max !== undefined && d.max !== "") ? (
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                Rango aceptado: {d.min ?? "−∞"} → {d.max ?? "+∞"}
              </div>
            ) : null}
          </>
        );
        break;
      case "askPhone":
        content = (
          <>
            {questionProps()}
            <div style={{ marginTop: 4 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>
                Formato de teléfono
              </label>
              <select value={d.phoneFormat || "any"} onChange={e => upd("phoneFormat", e.target.value)} style={inputSt}>
                <option value="any">Cualquier formato</option>
                <option value="international">Internacional (+XX XXXXXXXXXX)</option>
                <option value="local">Local (10 dígitos)</option>
                <option value="e164">E.164 (+XXXXXXXXXXX sin espacios)</option>
              </select>
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
                {d.phoneFormat === "international" && "Ej: +54 9 11 12345678"}
                {d.phoneFormat === "local" && "Ej: 1112345678 (10 dígitos)"}
                {d.phoneFormat === "e164" && "Ej: +5491112345678"}
                {(!d.phoneFormat || d.phoneFormat === "any") && "Acepta cualquier número válido"}
              </div>
            </div>
          </>
        );
        break;
      case "conditions":
        content = (
          <>
            {(d.conditions || []).map((c, i) => (
              <div key={i} style={{ border: "1px solid #334155", borderRadius: 6, padding: 8, marginBottom: 8 }}>
                <VarSel label={t("ui.variable")} value={c.variable}
                  onChange={v => { const nc = [...d.conditions]; nc[i] = { ...nc[i], variable: v }; upd("conditions", nc); }}
                  vars={vars} onAddVar={quickAddVar} t={t} />
                <select value={c.operator} onChange={e => {
                  const nc = [...d.conditions]; nc[i] = { ...nc[i], operator: e.target.value }; upd("conditions", nc);
                }} style={{ ...inputSt, marginBottom: 8 }}>
                  <option value="equals">= Igual a</option>
                  <option value="not_equals">≠ Diferente de</option>
                  <option value="text_equals">= Es texto igual a</option>
                  <option value="contains">Contiene</option>
                  <option value="greater_than">&gt; Mayor que</option>
                  <option value="less_than">&lt; Menor que</option>
                  <option value="length_gt">Longitud &gt; mayor que</option>
                  <option value="length_lt">Longitud &lt; menor que</option>
                  <option value="is_set">Está definida</option>
                  <option value="is_not_set">No está definida</option>
                </select>
                <TInput label={t("ui.value")} value={c.value}
                  onChange={v => { const nc = [...d.conditions]; nc[i] = { ...nc[i], value: v }; upd("conditions", nc); }} />
                <VarInsert value={c.value} onChange={v => { const nc = [...d.conditions]; nc[i] = { ...nc[i], value: v }; upd("conditions", nc); }} vars={vars} />
              </div>
            ))}
            <button onClick={() => upd("conditions", [...(d.conditions || []), { variable: "", operator: "equals", value: "" }])}
              style={addBtnSt}>+ Agregar condición</button>
          </>
        );
        break;
      case "setField":
        content = (
          <>
            <VarSel label={t("ui.variable")} value={d.variable}
              onChange={v => upd("variable", v)} vars={vars} onAddVar={quickAddVar} t={t} />
            <TInput label={t("ui.fieldValue")} value={d.value} onChange={v => upd("value", v)}
              ph={t("ui.varHint")} />
            <VarInsert value={d.value} onChange={v => upd("value", v)} vars={vars} />
          </>
        );
        break;
      case "formulas":
        content = (
          <>
            <TInput label={t("ui.formula")} value={d.formula} onChange={v => upd("formula", v)}
              ph="{{precio}} * {{cantidad}}" />
            <VarInsert value={d.formula} onChange={v => upd("formula", v)} vars={vars} />
            <VarSel label={t("ui.resultVar")} value={d.resultVariable}
              onChange={v => upd("resultVariable", v)} vars={vars} onAddVar={quickAddVar} t={t} />
          </>
        );
        break;
      case "jumpTo":
        content = (
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>
              {t("ui.targetNode")}
            </label>
            <select value={d.targetNodeId || ""} onChange={e => upd("targetNodeId", e.target.value)} style={inputSt}>
              <option value="">--</option>
              {nodes.filter(n => n.id !== selNode.id).map(n => (
                <option key={n.id} value={n.id}>{t(`node.${n.type}`)} ({n.id.slice(0, 8)})</option>
              ))}
            </select>
          </div>
        );
        break;
      case "keywordJump":
        content = (
          <TInput label={t("ui.keywords")} value={(d.keywords || []).join(", ")}
            onChange={v => upd("keywords", v.split(",").map(s => s.trim()))}
            ph="hola, inicio, empezar" />
        );
        break;
      case "globalKeywords":
        content = (
          <>
            {(d.keywords || []).map((kw, i) => (
              <TInput key={i} label={`${t("ui.keyword")} ${i + 1}`} value={kw.keyword}
                onChange={v => { const nk = [...d.keywords]; nk[i] = { ...nk[i], keyword: v }; upd("keywords", nk); }} />
            ))}
            <button onClick={() => upd("keywords", [...(d.keywords || []), { keyword: "", targetNodeId: "" }])}
              style={addBtnSt}>+ {t("ui.addKeyword")}</button>
            <VarSel label={t("ui.saveVar")} value={d.saveResponseTo}
              onChange={v => upd("saveResponseTo", v)} vars={vars} onAddVar={quickAddVar} t={t} />
          </>
        );
        break;
      case "leadScoring":
        content = (
          <>
            <TInput label={t("ui.score")} value={d.scoreChange} onChange={v => upd("scoreChange", Number(v))} ph="+10 o -5" type="number" />
            <VarSel label="Variable de score" value={d.scoreVariable}
              onChange={v => upd("scoreVariable", v)} vars={vars} onAddVar={quickAddVar} t={t} />
          </>
        );
        break;
      case "goal":
        content = (
          <>
            <TInput label="Nombre del objetivo" value={d.goalName} onChange={v => upd("goalName", v)} />
            <VarSel label={t("ui.saveVar")} value={d.saveResponseTo}
              onChange={v => upd("saveResponseTo", v)} vars={vars} onAddVar={quickAddVar} t={t} />
          </>
        );
        break;
      case "abTest":
        content = (
          <>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>
                {t("ui.percentage")} A: {d.percentageA || 50}%
              </label>
              <input type="range" min={10} max={90} value={d.percentageA || 50}
                onChange={e => upd("percentageA", Number(e.target.value))}
                style={{ width: "100%", accentColor: "#f97316" }} />
            </div>
            <VarSel label="Guardar variante en" value={d.saveVariantTo}
              onChange={v => upd("saveVariantTo", v)} vars={vars} onAddVar={quickAddVar} t={t} />
          </>
        );
        break;
      // ── Integrations ──────────────────────────────────────
      case "integrationEntry":
        content = (
          <div style={{ padding: "8px 0", color: "#64748b", fontSize: 11, lineHeight: 1.6 }}>
            <p style={{ margin: 0 }}>Este nodo marca el <strong style={{ color: "#0ea5e9" }}>inicio</strong> de un bloque de integración.</p>
            <p style={{ margin: "8px 0 0" }}>Conectá la salida de un nodo del flujo a este nodo, luego conectá este nodo a un nodo <strong style={{ color: "#0ea5e9" }}>API</strong> o <strong style={{ color: "#0ea5e9" }}>BD</strong>.</p>
          </div>
        );
        break;

      case "integrationExit":
        content = (
          <div style={{ padding: "8px 0", color: "#64748b", fontSize: 11, lineHeight: 1.6 }}>
            <p style={{ margin: 0 }}>Este nodo marca el <strong style={{ color: "#0ea5e9" }}>fin</strong> del bloque de integración.</p>
            <p style={{ margin: "8px 0 0" }}>Conectá la salida del nodo API o BD a este nodo, luego conectá este nodo de vuelta al flujo principal.</p>
          </div>
        );
        break;

      case "apiCall":
        content = (
          <>
            {/* URL + Método */}
            <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 6, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Método</label>
                <select value={d.method || "GET"} onChange={e => upd("method", e.target.value)} style={inputSt}>
                  <option>GET</option><option>POST</option><option>PUT</option><option>PATCH</option><option>DELETE</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>URL</label>
                <input value={d.url} onChange={e => upd("url", e.target.value)}
                  placeholder="https://api.ejemplo.com/endpoint"
                  style={{ ...inputSt, fontFamily: "monospace", fontSize: 10 }} />
              </div>
            </div>

            {/* Autenticación */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Autenticación</label>
              <select value={d.authType || "none"} onChange={e => upd("authType", e.target.value)} style={{ ...inputSt, marginBottom: 6 }}>
                <option value="none">Sin autenticación</option>
                <option value="bearer">Bearer Token</option>
                <option value="basic">Basic Auth (user:pass)</option>
                <option value="apikey">API Key (header)</option>
              </select>
              {d.authType && d.authType !== "none" && (
                <input value={d.authValue || ""} onChange={e => upd("authValue", e.target.value)}
                  placeholder={d.authType === "bearer" ? "Token..." : d.authType === "basic" ? "usuario:contraseña" : "Nombre: Valor"}
                  style={{ ...inputSt, fontFamily: "monospace", fontSize: 10 }} />
              )}
            </div>

            {/* Headers */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Headers (JSON)</label>
              <textarea value={d.headers || "{}"} onChange={e => upd("headers", e.target.value)} rows={3}
                placeholder={'{"Content-Type": "application/json"}'}
                style={{ ...inputSt, fontFamily: "monospace", fontSize: 10, resize: "vertical", minHeight: 56 }} />
            </div>

            {/* Body */}
            {(d.method === "POST" || d.method === "PUT" || d.method === "PATCH") && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Body (JSON / texto)</label>
                <textarea value={d.body || ""} onChange={e => upd("body", e.target.value)} rows={4}
                  placeholder={'{"campo": "{{variable}}"}'}
                  style={{ ...inputSt, fontFamily: "monospace", fontSize: 10, resize: "vertical", minHeight: 70 }} />
              </div>
            )}

            {/* Timeout */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Timeout (segundos)</label>
              <input type="number" min={1} max={60} value={d.timeout ?? 10} onChange={e => upd("timeout", Number(e.target.value))}
                style={{ ...inputSt, width: 80 }} />
            </div>

            {/* Variable de respuesta */}
            <VarSel label="Guardar respuesta en variable" value={d.saveResponseTo}
              onChange={v => upd("saveResponseTo", v)} vars={vars} onAddVar={quickAddVar} t={t} />

            <div style={{ marginTop: 8, padding: "8px", background: "#0f172a", borderRadius: 6, fontSize: 10, color: "#475569" }}>
              💡 Salida <strong style={{ color: "#22c55e" }}>success</strong>: respuesta OK · Salida <strong style={{ color: "#ef4444" }}>error</strong>: timeout o error HTTP
            </div>

            {/* Botones Probar */}
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <button
                onClick={() => runApiTest(d, "proxy")}
                disabled={apiTest.loading || !d.url}
                title="Llama via Azure Functions (sin CORS)"
                style={{
                  flex: 1, padding: "8px 0",
                  background: apiTest.loading && apiTest.mode === "proxy" ? "#1e3a5f" : "#1e40af",
                  border: "none", borderRadius: 6, color: "#fff",
                  fontSize: 11, fontWeight: 600, cursor: d.url ? "pointer" : "not-allowed",
                  opacity: d.url ? 1 : 0.5,
                }}
              >
                {apiTest.loading && apiTest.mode === "proxy" ? "⏳ Llamando..." : "▶ By Proxy"}
              </button>
              <button
                onClick={() => runApiTest(d, "local")}
                disabled={apiTest.loading || !d.url}
                title="Llama via localhost:7071 (func start local)"
                style={{
                  flex: 1, padding: "8px 0",
                  background: apiTest.loading && apiTest.mode === "local" ? "#3b1f5f" : "#5b21b6",
                  border: "none", borderRadius: 6, color: "#fff",
                  fontSize: 11, fontWeight: 600, cursor: d.url ? "pointer" : "not-allowed",
                  opacity: d.url ? 1 : 0.5,
                }}
              >
                {apiTest.loading && apiTest.mode === "local" ? "⏳ Llamando..." : "▶ Localhost"}
              </button>
            </div>

            {/* Resultado del test */}
            {(apiTest.status !== null || apiTest.error) && (
              <div style={{ marginTop: 8, borderRadius: 6, overflow: "hidden", fontSize: 10 }}>
                <div style={{
                  padding: "5px 8px", fontWeight: 700,
                  background: apiTest.error || apiTest.status >= 400 ? "#7f1d1d" : "#14532d",
                  color: apiTest.error || apiTest.status >= 400 ? "#fca5a5" : "#86efac",
                }}>
                  {apiTest.error ? `❌ Error: ${apiTest.error}` : `${apiTest.status >= 400 ? "❌" : "✅"} HTTP ${apiTest.status}`}
                  <span style={{ fontWeight: 400, marginLeft: 6, opacity: 0.7 }}>
                    [{apiTest.mode === "local" ? "localhost" : "proxy"}]
                  </span>
                </div>
                {apiTest.body && (
                  <pre style={{
                    margin: 0, padding: "8px", background: "#0f172a", color: "#94a3b8",
                    fontFamily: "monospace", fontSize: 10, maxHeight: 180,
                    overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
                  }}>
                    {apiTest.body}
                  </pre>
                )}
              </div>
            )}
          </>
        );
        break;

      case "dbQuery":
        content = (
          <>
            {/* Tipo de BD */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Motor de base de datos</label>
              <select value={d.dbType || "postgres"} onChange={e => upd("dbType", e.target.value)} style={inputSt}>
                <option value="postgres">PostgreSQL</option>
                <option value="mysql">MySQL / MariaDB</option>
                <option value="sqlserver">SQL Server</option>
                <option value="sqlite">SQLite</option>
                <option value="mongodb">MongoDB</option>
                <option value="redis">Redis</option>
              </select>
            </div>

            {/* Cadena de conexión */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Cadena de conexión</label>
              <input value={d.connectionString || ""} onChange={e => upd("connectionString", e.target.value)}
                placeholder={d.dbType === "mongodb" ? "mongodb://user:pass@host:27017/db" : "postgresql://user:pass@host:5432/db"}
                style={{ ...inputSt, fontFamily: "monospace", fontSize: 10 }} />
            </div>

            {/* Query */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>
                {d.dbType === "mongodb" ? "Filtro / Comando (JSON)" : "Consulta SQL"}
              </label>
              <textarea value={d.query || ""} onChange={e => upd("query", e.target.value)} rows={4}
                placeholder={d.dbType === "mongodb" ? '{"collection": "usuarios", "find": {"id": "{{id}}"}}' : 'SELECT * FROM usuarios WHERE telefono = \'{{telefono}}\''}
                style={{ ...inputSt, fontFamily: "monospace", fontSize: 10, resize: "vertical", minHeight: 80 }} />
            </div>

            {/* Parámetros adicionales */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Parámetros (JSON, opcional)</label>
              <textarea value={d.params || ""} onChange={e => upd("params", e.target.value)} rows={2}
                placeholder={'{"limit": 1, "timeout": 5000}'}
                style={{ ...inputSt, fontFamily: "monospace", fontSize: 10, resize: "vertical", minHeight: 44 }} />
            </div>

            {/* Variable de resultado */}
            <VarSel label="Guardar resultado en variable" value={d.saveResponseTo}
              onChange={v => upd("saveResponseTo", v)} vars={vars} onAddVar={quickAddVar} t={t} />

            <div style={{ marginTop: 8, padding: "8px", background: "#0f172a", borderRadius: 6, fontSize: 10, color: "#475569" }}>
              💡 Salida <strong style={{ color: "#22c55e" }}>success</strong>: consulta OK · Salida <strong style={{ color: "#ef4444" }}>error</strong>: fallo de conexión o query
            </div>
          </>
        );
        break;

      // ── Integración Generador ────────────────────────────────
      case "textMenu":
        content = (
          <>
            <TInput label="Mensaje del menú" value={d.message} onChange={v => upd("message", v)} multi maxLength={1024} />
            <VarInsert value={d.message} onChange={v => upd("message", v)} vars={vars} />
            <div style={{ margin: "10px 0 4px", fontSize: 10, color: "#94a3b8" }}>Opciones (máx. 15)</div>
            {(d.options || []).map((opt, i) => (
              <div key={opt.id} style={{ display: "flex", gap: 4, marginBottom: 4, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: "#475569", minWidth: 20 }}>{i + 1}.</span>
                <input value={opt.keyword} onChange={e => {
                  const no = [...d.options]; no[i] = { ...no[i], keyword: e.target.value };
                  upd("options", no);
                }} placeholder="Palabra clave" style={{ ...inputSt, flex: 1 }} />
                <input value={opt.label} onChange={e => {
                  const no = [...d.options]; no[i] = { ...no[i], label: e.target.value };
                  upd("options", no);
                }} placeholder="Etiqueta" style={{ ...inputSt, flex: 1 }} />
                {(d.options || []).length > 1 && (
                  <button onClick={() => upd("options", d.options.filter((_, j) => j !== i))} style={delBtnSt}>×</button>
                )}
              </div>
            ))}
            {(d.options || []).length < 15 && (
              <button onClick={() => {
                const idx = (d.options || []).length;
                upd("options", [...(d.options || []), { id: `tm_${idx}`, keyword: "", label: `Opción ${idx + 1}` }]);
              }} style={addBtnSt}>+ Agregar opción</button>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
              <div>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Reintentos</label>
                <input type="number" min={1} max={10} value={d.retries ?? 3}
                  onChange={e => upd("retries", Number(e.target.value))} style={{ ...inputSt, padding: "5px 7px" }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Timeout (seg)</label>
                <input type="number" min={5} max={300} value={d.timeoutSecs ?? 20}
                  onChange={e => upd("timeoutSecs", Number(e.target.value))} style={{ ...inputSt, padding: "5px 7px" }} />
              </div>
            </div>
            <VarSel label="Guardar respuesta en" value={d.saveResponseTo}
              onChange={v => upd("saveResponseTo", v)} vars={vars} onAddVar={quickAddVar} t={t} />
          </>
        );
        break;

      case "transferAgent":
        content = (
          <>
            <TInput label="Cola / Skill de destino" value={d.queue} onChange={v => upd("queue", v)} ph="Ej: Ventas, Soporte" />
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Prioridad</label>
              <select value={d.priority || "normal"} onChange={e => upd("priority", e.target.value)} style={inputSt}>
                <option value="low">Baja</option>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
            <div style={{ marginTop: 8, padding: 8, background: "#0f172a", borderRadius: 6, fontSize: 10, color: "#475569" }}>
              💡 Salida <strong style={{ color: "#22c55e" }}>transferido</strong>: agente disponible ·
              Salida <strong style={{ color: "#ef4444" }}>sin operadores</strong>: no hay agentes
            </div>
          </>
        );
        break;

      case "acdSkill":
        content = (
          <>
            <TInput label="Habilidad / Cola ACD" value={d.skill} onChange={v => upd("skill", v)} ph="Ej: _Bariloche Turnos" />
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Habilidades disponibles (una por línea)</label>
              <textarea
                value={(d.skills || []).join("\n")}
                onChange={e => upd("skills", e.target.value.split("\n").map(s => s.trim()).filter(Boolean))}
                rows={5}
                style={{ ...inputSt, width: "100%", resize: "vertical", fontFamily: "monospace", fontSize: 11 }}
                placeholder={"_Allen Turnos\n_Bariloche Farmacias\n_Bolson"}
              />
            </div>
            <div style={{ marginTop: 8, padding: 8, background: "#0f172a", borderRadius: 6, fontSize: 10, color: "#475569" }}>
              💡 Setea la habilidad ACD antes de un nodo <strong style={{ color: "#dc2626" }}>Transferir a Agente</strong>
            </div>
          </>
        );
        break;

      case "groqChat":
        content = (
          <>
            <div style={{ padding: 10, background: "#3b0764", borderRadius: 8, fontSize: 11, color: "#c4b5fd", marginBottom: 8 }}>
              💬 <strong>Agente Groq IA</strong><br />
              <span style={{ color: "#7c3aed", fontSize: 10 }}>La configuración (API key, modelo, prompt) se gestiona desde el panel principal del bot 🤖</span>
            </div>
            <VarSel label="Guardar última respuesta en variable" value={d.outputVariable}
              onChange={v => upd("outputVariable", v)} vars={vars} onAddVar={quickAddVar} t={t} />
            <div style={{ marginTop: 8, padding: 8, background: "#0f172a", borderRadius: 6, fontSize: 10, color: "#475569" }}>
              💡 Salida <strong style={{ color: "#22c55e" }}>ok</strong>: usuario escribió la palabra clave de salida ·
              Salida <strong style={{ color: "#ef4444" }}>error</strong>: sin API key o fallo de conexión
            </div>
          </>
        );
        break;

      case "calendar": {
        const DAY_NAMES = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];
        const toggleDay = (idx) => {
          const cur = d.days || [];
          upd("days", cur.includes(idx) ? cur.filter(d => d !== idx) : [...cur, idx].sort());
        };
        const addHoliday = (date) => {
          if (!date) return;
          const cur = d.holidays || [];
          if (!cur.includes(date)) upd("holidays", [...cur, date].sort());
        };
        const removeHoliday = (date) => upd("holidays", (d.holidays||[]).filter(h => h !== date));
        content = (
          <>
            {/* Pausar rama */}
            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer",
              padding:"10px 12px", borderRadius:8, marginBottom:8,
              background: d.paused ? "rgba(239,68,68,0.1)" : "#0f172a",
              border: `1px solid ${d.paused ? "#ef4444" : "#1e293b"}` }}>
              <input type="checkbox" checked={!!d.paused} onChange={e => upd("paused", e.target.checked)}
                style={{ width:16, height:16, accentColor:"#ef4444", cursor:"pointer" }} />
              <span style={{ fontSize:13, fontWeight:700, color: d.paused ? "#f87171" : "#e2e8f0" }}>
                ⏸ Pausar rama (siempre devuelve false)
              </span>
            </label>

            {/* Días habilitados */}
            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:10, color:"#94a3b8", display:"block", marginBottom:6 }}>Días habilitados</label>
              <div style={{ display:"flex", gap:5 }}>
                {DAY_NAMES.map((name, idx) => {
                  const on = (d.days||[]).includes(idx);
                  return (
                    <button key={idx} onClick={() => toggleDay(idx)} style={{
                      flex:1, padding:"5px 0", borderRadius:6, border:"none", cursor:"pointer", fontSize:10, fontWeight:700,
                      background: on ? "#0369a1" : "#1e293b",
                      color: on ? "#fff" : "#475569",
                    }}>{name}</button>
                  );
                })}
              </div>
            </div>

            {/* Rango horario */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
              <div>
                <label style={{ fontSize:10, color:"#94a3b8", display:"block", marginBottom:3 }}>Hora inicio</label>
                <input type="time" value={d.timeStart||"08:00"} onChange={e => upd("timeStart", e.target.value)} style={inputSt} />
              </div>
              <div>
                <label style={{ fontSize:10, color:"#94a3b8", display:"block", marginBottom:3 }}>Hora fin</label>
                <input type="time" value={d.timeEnd||"18:00"} onChange={e => upd("timeEnd", e.target.value)} style={inputSt} />
              </div>
            </div>

            {/* Feriados */}
            <div>
              <label style={{ fontSize:10, color:"#94a3b8", display:"block", marginBottom:6 }}>Feriados</label>
              <div style={{ display:"flex", gap:6, marginBottom:6 }}>
                <input type="date" id={`cal_hday_${selNode.id}`} style={{ ...inputSt, flex:1 }} />
                <button onClick={() => {
                  const el = document.getElementById(`cal_hday_${selNode.id}`);
                  if (el?.value) { addHoliday(el.value); el.value = ""; }
                }} style={{ padding:"0 12px", borderRadius:6, border:"none", background:"#0369a1", color:"#fff", cursor:"pointer", fontSize:12, fontWeight:600 }}>
                  + Agregar
                </button>
              </div>
              <div style={{ maxHeight:120, overflowY:"auto", display:"flex", flexDirection:"column", gap:3 }}>
                {(d.holidays||[]).length === 0 && (
                  <div style={{ fontSize:11, color:"#334155", fontStyle:"italic" }}>Sin feriados cargados</div>
                )}
                {(d.holidays||[]).map(h => (
                  <div key={h} style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"4px 8px", background:"#0f172a", borderRadius:5, fontSize:11, color:"#94a3b8" }}>
                    <span>{h}</span>
                    <button onClick={() => removeHoliday(h)} style={{ background:"none", border:"none", color:"#ef4444", cursor:"pointer", fontSize:14, lineHeight:1 }}>×</button>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop:8, padding:8, background:"#0f172a", borderRadius:6, fontSize:10, color:"#475569" }}>
              💡 Salida <strong style={{ color:"#22c55e" }}>true</strong>: dentro del horario y no es feriado ·
              Salida <strong style={{ color:"#ef4444" }}>false</strong>: fuera de horario, feriado o pausado
            </div>
          </>
        );
        break;
      }

      case "flowControl": {
        const mappings = d.mappings || [];
        const updMap = (idx, field, val) => {
          const next = mappings.map((m, i) => i === idx ? { ...m, [field]: val } : m);
          upd("mappings", next);
        };
        content = (
          <>
            <div style={{ padding: "8px 10px", background: "#2e1065", borderRadius: 8, marginBottom: 10, fontSize: 11, color: "#c4b5fd" }}>
              🔀 <strong>Control de Flujo</strong><br />
              <span style={{ color: "#7c3aed", fontSize: 10 }}>
                Lee el recorrido del usuario (<code style={{ background: "#1e1040", padding: "1px 4px", borderRadius: 3 }}>__FLOW_TRACE__</code>) y crea variables con los valores capturados.
                El nombre del nodo debe coincidir exactamente con el <strong>label personalizado</strong> del objeto que capturó el input.
              </span>
            </div>

            {mappings.length === 0 && (
              <div style={{ fontSize: 11, color: "#334155", fontStyle: "italic", marginBottom: 8 }}>
                Sin mapeos configurados
              </div>
            )}

            {mappings.map((m, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <input
                    value={m.traceName || ""}
                    onChange={e => updMap(i, "traceName", e.target.value)}
                    placeholder="Label del nodo origen"
                    style={{ ...inputSt, fontSize: 11 }}
                  />
                </div>
                <span style={{ color: "#475569", fontSize: 12, flexShrink: 0 }}>→</span>
                <div style={{ flex: 1 }}>
                  <input
                    value={m.targetVar || ""}
                    onChange={e => updMap(i, "targetVar", e.target.value.replace(/[{}]/g, ""))}
                    placeholder="NOMBRE_VARIABLE"
                    style={{ ...inputSt, fontSize: 11, fontFamily: "monospace", textTransform: "uppercase" }}
                  />
                </div>
                <button onClick={() => upd("mappings", mappings.filter((_, j) => j !== i))}
                  style={delBtnSt}>×</button>
              </div>
            ))}

            <button
              onClick={() => upd("mappings", [...mappings, { traceName: "", targetVar: "" }])}
              style={addBtnSt}>+ Agregar mapeo</button>

            <div style={{ marginTop: 10, padding: 8, background: "#0f172a", borderRadius: 6, fontSize: 10, color: "#475569" }}>
              💡 Los objetos que generan traza son los que tienen un <strong style={{ color: "#c4b5fd" }}>label personalizado</strong> (campo "nombre" del panel):
              menús, preguntas, botones y listas. Las imágenes se ignoran.
            </div>
          </>
        );
        break;
      }

      case "loopControl":
        content = (
          <>
            <VarSel label="Variable de conteo" value={d.variable}
              onChange={v => upd("variable", v)} vars={vars} onAddVar={quickAddVar} t={t} />
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Máximo de ciclos</label>
              <input type="number" min={1} max={20} value={d.maxCycles ?? 3}
                onChange={e => upd("maxCycles", Number(e.target.value))} style={{ ...inputSt, width: 80 }} />
            </div>
            <div style={{ marginTop: 8, padding: 8, background: "#0f172a", borderRadius: 6, fontSize: 10, color: "#475569" }}>
              💡 Salida <strong style={{ color: "#a78bfa" }}>ciclo incompleto</strong>: aún no alcanzó el límite ·
              Salida <strong style={{ color: "#22c55e" }}>ciclo completo</strong>: límite alcanzado
            </div>
          </>
        );
        break;

      case "aiBot":
        content = (
          <>
            <TInput label="Nombre del asistente (ID)" value={d.assistantName} onChange={v => upd("assistantName", v)} ph="Ej: BOT_CYT" />
            <TInput label="Thread de bienvenida" value={d.welcomeThread} onChange={v => upd("welcomeThread", v)} ph="Ej: BOT_CYT_WELCOME" />
            <TInput label="Variable de desconexión" value={d.disconnectVar} onChange={v => upd("disconnectVar", v)} ph="Ej: chat_disconnect" />
            <TInput label="Variable de asistencia humana" value={d.assistanceVar} onChange={v => upd("assistanceVar", v)} ph="Ej: RequiereAsistencia" />
            <VarSel label="Guardar respuesta IA en" value={d.outputVariable}
              onChange={v => upd("outputVariable", v)} vars={vars} onAddVar={quickAddVar} t={t} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 8 }}>
              <div>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Timeout (seg)</label>
                <input type="number" min={1} max={60} value={d.timeout ?? 5}
                  onChange={e => upd("timeout", Number(e.target.value))} style={{ ...inputSt, padding: "5px 7px" }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Max tokens</label>
                <input type="number" min={50} max={4000} value={d.maxTokens ?? 250}
                  onChange={e => upd("maxTokens", Number(e.target.value))} style={{ ...inputSt, padding: "5px 7px" }} />
              </div>
              <div>
                <label style={{ fontSize: 10, color: "#94a3b8", display: "block", marginBottom: 3 }}>Sleep (ms)</label>
                <input type="number" min={0} max={5000} value={d.sleepTime ?? 750}
                  onChange={e => upd("sleepTime", Number(e.target.value))} style={{ ...inputSt, padding: "5px 7px" }} />
              </div>
            </div>
            <div style={{ marginTop: 8, padding: 8, background: "#0f172a", borderRadius: 6, fontSize: 10, color: "#475569", lineHeight: 1.6 }}>
              7 salidas: Desconexión · Finaliza OK · Requiere asistencia · Tag extra · Dígito finalizador · Sin recursos · Error
            </div>
          </>
        );
        break;

      default: content = null;
    }

    return (
      <div style={{
        position: "absolute", right: 0, top: 0, bottom: 0, width: 310,
        background: "#0f172a", borderLeft: "1px solid #1e293b",
        overflowY: "auto", zIndex: 30, padding: 14,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: "#e2e8f0", fontSize: 13 }}>
            {def.icon} {t(`node.${selNode.type}`)}
          </h3>
          <button onClick={() => setPropsOpen(false)}
            style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
        <div style={{ fontSize: 10, color: "#475569", marginBottom: 10, fontFamily: "monospace" }}>
          ID: {selNode.id}
        </div>
        {(() => {
          // Campo principal del nodo según tipo
          const mainKey = ["sendMessage","goodbye","media"].includes(selNode.type) ? (selNode.type === "media" ? "caption" : "message")
            : ["replyButtons","listButtons"].includes(selNode.type) ? "body"
            : ["askQuestion","askEmail","askNumber","askPhone","askDate","askFile","askAddress","askUrl"].includes(selNode.type) ? "question"
            : null;

          const labelVal  = d.label || "";
          const mainVal   = mainKey ? (d[mainKey] || "") : "";
          const canLabel2Main = mainKey && labelVal && !mainVal;
          const canMain2Label = mainKey && mainVal && !labelVal;

          return (
            <>
              <TInput
                label="Nombre del nodo"
                value={labelVal}
                onChange={v => upd("label", v)}
                ph={t(`node.${selNode.type}`)}
              />
              {canLabel2Main && (
                <button onClick={() => upd(mainKey, labelVal)}
                  style={suggBtnSt}>
                  ↓ Usar como {mainKey === "body" ? "cuerpo" : mainKey === "caption" ? "caption" : mainKey === "question" ? "pregunta" : "mensaje"}
                </button>
              )}
              {canMain2Label && (
                <button onClick={() => upd("label", mainVal.slice(0, 60))}
                  style={suggBtnSt}>
                  ↑ Usar como nombre del nodo
                </button>
              )}
            </>
          );
        })()}
        <div style={{ borderTop: "1px solid #1e293b", marginBottom: 12, marginTop: 4 }} />
        {content}
        <div style={{ borderTop: "1px solid #1e293b", marginTop: 14, paddingTop: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: "#94a3b8" }}>
            <input
              type="checkbox"
              checked={d.generateLog || false}
              onChange={e => upd("generateLog", e.target.checked)}
              style={{ accentColor: "#3b82f6", width: 14, height: 14 }}
            />
            Genera log
          </label>
        </div>
      </div>
    );
  };

  // ============================================================
  // RENDER PRINCIPAL
  // ============================================================
  return (
    <div style={{
      width: "100%", height: "100vh", display: "flex", background: "#0a0e1a",
      fontFamily: "'Segoe UI',system-ui,-apple-system,sans-serif",
      color: "#e2e8f0", overflow: "hidden", position: "relative",
    }}>
      {/* ========== SIDEBAR IZQUIERDO ========== */}
      <div style={{
        width: sideOpen ? 256 : 44, minWidth: sideOpen ? 256 : 44,
        background: "#0f172a", borderRight: "1px solid #1e293b",
        display: "flex", flexDirection: "column",
        transition: "width 0.2s, min-width 0.2s", overflow: "hidden", zIndex: 20,
      }}>
        {/* ── Header del sidebar: logo + toggle ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: sideOpen ? "14px 12px 10px" : "14px 8px 10px",
          borderBottom: "1px solid #1e293b",
          minHeight: 56,
        }}>
          {sideOpen && (
            /* Logo CYT Comunicaciones SVG — fondo transparente, optimizado para dark */
            <svg viewBox="0 0 220 52" width="148" height="35" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <defs>
                <linearGradient id="cyt-g1" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#a855f7"/>
                  <stop offset="100%" stopColor="#7c3aed"/>
                </linearGradient>
                <linearGradient id="cyt-g2" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#a855f7"/>
                  <stop offset="100%" stopColor="#818cf8"/>
                </linearGradient>
              </defs>
              {/* Ícono red de nodos */}
              <circle cx="10" cy="26" r="4.5" fill="url(#cyt-g1)"/>
              <circle cx="28" cy="10" r="5.5" fill="url(#cyt-g1)"/>
              <circle cx="28" cy="42" r="3.5" fill="#7c3aed" opacity="0.7"/>
              <line x1="13.5" y1="23.5" x2="24" y2="13" stroke="url(#cyt-g1)" strokeWidth="1.8" strokeLinecap="round"/>
              <line x1="13.5" y1="28" x2="25" y2="39.5" stroke="#7c3aed" strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
              <line x1="28" y1="15.5" x2="28" y2="38.5" stroke="#a855f7" strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
              {/* CYT */}
              <text x="40" y="36" fontFamily="'Segoe UI', system-ui, sans-serif" fontWeight="800" fontSize="30" fill="url(#cyt-g2)" letterSpacing="-0.5">CYT</text>
              {/* COMUNICACIONES */}
              <text x="41" y="48" fontFamily="'Segoe UI', system-ui, sans-serif" fontWeight="500" fontSize="9.5" fill="#64748b" letterSpacing="2.5">COMUNICACIONES</text>
            </svg>
          )}
          <button onClick={() => setSideOpen(!sideOpen)}
            style={{ background: "none", border: "none", color: "#475569", padding: "4px 6px",
              cursor: "pointer", fontSize: 13, marginLeft: sideOpen ? 4 : 0, flexShrink: 0,
              borderRadius: 5, lineHeight: 1 }}
            title={sideOpen ? "Contraer panel" : "Expandir panel"}>
            {sideOpen ? "◀" : "▶"}
          </button>
        </div>

        {sideOpen && (
          <>
            {/* Nombre del bot editable */}
            <div style={{ padding: "10px 14px 6px" }}>
              <input value={botName} onChange={e => setBotName(e.target.value)}
                style={{ width: "100%", background: "none", border: "none", color: "#94a3b8",
                  fontSize: 12, fontWeight: 600, padding: 0, outline: "none", letterSpacing: "0.02em" }} />
            </div>

            {/* Búsqueda */}
            <div style={{ padding: "0 14px 10px" }}>
              <input placeholder={t("ui.search")} value={search} onChange={e => setSearch(e.target.value)}
                style={{ ...inputSt, padding: "7px 10px" }} />
            </div>

            {/* Categorías con nodos arrastrables */}
            <div style={{ flex: 1, overflowY: "auto", padding: "0 6px" }}>
              {CATS.map(cat => {
                const filtered = cat.nodes.filter(nt => {
                  if (!search) return true;
                  return t(`node.${nt}`).toLowerCase().includes(search.toLowerCase());
                });
                if (filtered.length === 0 && search) return null;

                return (
                  <div key={cat.id} style={{ marginBottom: 2 }}>
                    {/* Botón de categoría */}
                    <button onClick={() => setExpCat(expCat === cat.id ? null : cat.id)}
                      style={{
                        width: "100%", display: "flex", justifyContent: "space-between",
                        alignItems: "center", padding: "9px 8px",
                        background: expCat === cat.id ? "#1e293b" : "transparent",
                        border: "none", borderRadius: 8, color: "#e2e8f0",
                        cursor: "pointer", fontSize: 12, fontWeight: 600,
                      }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {t(cat.lk)}
                        {cat.id === "integrations" && (
                          <span style={{
                            fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 4,
                            background: "linear-gradient(135deg, #0ea5e9, #6366f1)",
                            color: "#fff", letterSpacing: "0.5px", textTransform: "uppercase",
                          }}>Nuevo</span>
                        )}
                      </span>
                      <span style={{ fontSize: 9, color: "#64748b" }}>{expCat === cat.id ? "▼" : "▶"}</span>
                    </button>

                    {/* Nodos de la categoría (arrastrables) */}
                    {(expCat === cat.id || search) && (
                      <div style={{ padding: "2px 0 2px 4px" }}>
                        {filtered.map(nt => {
                          const def = NDEFS[nt];
                          return (
                            <div key={nt} draggable
                              onDragStart={e => e.dataTransfer.setData("nodeType", nt)}
                              style={{
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "7px 8px", borderRadius: 6, cursor: "grab",
                                marginBottom: 1, border: "1px solid transparent",
                                transition: "all 0.12s",
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = "#1e293b"; e.currentTarget.style.borderColor = "#334155"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; }}
                            >
                              <span style={{
                                width: 28, height: 28, borderRadius: 6,
                                background: `${def.color}22`, display: "flex",
                                alignItems: "center", justifyContent: "center",
                                fontSize: 14, flexShrink: 0,
                              }}>{def.icon}</span>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 600 }}>{t(`node.${nt}`)}</div>
                                <div style={{ fontSize: 9, color: "#64748b" }}>{t(`node.${nt}.desc`)}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Hint inferior */}
            <div style={{ padding: "8px 14px", borderTop: "1px solid #1e293b",
              color: "#475569", fontSize: 10, textAlign: "center" }}>
              ↕ {t("ui.dragHint")}
            </div>
          </>
        )}
      </div>

      {/* ========== CANVAS ========== */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>

        {/* TOOLBAR SUPERIOR */}
        <div style={{
          position: "absolute", top: 0, left: 0,
          right: propsOpen ? 310 : 0, height: 46,
          background: "rgba(10,14,26,0.96)", backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          display: "flex", alignItems: "center",
          padding: "0 10px", zIndex: 15, gap: 2,
        }}>

          {/* ── Grupo: Zoom ── */}
          <div style={tbGroup}>
            <TbBtn onClick={() => setScale(s => Math.max(s * 0.8, 0.15))} title="Alejar">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><rect x="2" y="5.5" width="8" height="1.2" rx="0.6"/></svg>
            </TbBtn>
            <span style={{ fontSize: 11, color: "#64748b", minWidth: 38, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
              {Math.round(scale * 100)}%
            </span>
            <TbBtn onClick={() => setScale(s => Math.min(s * 1.2, 3))} title="Acercar">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><rect x="2" y="5.5" width="8" height="1.2" rx="0.6"/><rect x="5.5" y="2" width="1.2" height="8" rx="0.6"/></svg>
            </TbBtn>
            <TbBtn onClick={() => { setScale(0.85); setOffset({ x: 0, y: 0 }); }} title="Restablecer vista">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M2 6a4 4 0 1 1 .9 2.5"/><path d="M2 9V6h3"/></svg>
            </TbBtn>
          </div>

          <TbSep />

          {/* ── Grupo: Undo / Redo ── */}
          <div style={tbGroup}>
            <TbBtn onClick={undo} disabled={!canUndo} title="Deshacer (Ctrl+Z)">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 5.5H8a3 3 0 0 1 0 6H5"/><path d="M2 5.5L4.5 3M2 5.5L4.5 8"/></svg>
            </TbBtn>
            <TbBtn onClick={redo} disabled={!canRedo} title="Rehacer (Ctrl+Y)">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5.5H5a3 3 0 0 0 0 6h3"/><path d="M11 5.5L8.5 3M11 5.5L8.5 8"/></svg>
            </TbBtn>
          </div>

          <TbSep />

          {/* ── Stats ── */}
          <div style={{ fontSize: 10, color: "#334155", whiteSpace: "nowrap", padding: "0 6px", letterSpacing: "0.02em" }}>
            <span style={{ color: "#475569" }}>{nodes.length}</span> nodos
            <span style={{ margin: "0 4px", color: "#1e293b" }}>·</span>
            <span style={{ color: "#475569" }}>{edges.length}</span> conexiones
            <span style={{ margin: "0 4px", color: "#1e293b" }}>·</span>
            <span style={{ color: "#475569" }}>{vars.length}</span> vars
          </div>

          {/* ── Indicador de guardado ── */}
          {syncStatus !== "idle" && (
            <div style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4, padding: "0 4px",
              color: syncStatus === "error" ? "#f87171" : syncStatus === "saved" ? "#34d399" : "#64748b" }}>
              {syncStatus === "saving" && (
                <svg width="10" height="10" viewBox="0 0 10 10" style={{ animation: "spin 1s linear infinite" }}>
                  <circle cx="5" cy="5" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 6"/>
                </svg>
              )}
              {syncStatus === "saved" && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 5l2.5 2.5L8 3"/>
                </svg>
              )}
              {syncStatus === "error" && "✕"}
              {syncStatus === "saving" ? "Guardando…" : syncStatus === "saved" ? "Guardado" : "Error al guardar"}
            </div>
          )}

          {/* ── Spacer ── */}
          <div style={{ flex: 1 }} />

          {/* ── Grupo: Idioma ── */}
          <select value={lang} onChange={e => setLang(e.target.value)}
            style={{ height: 28, padding: "0 6px", background: "#0f172a", border: "1px solid #1e293b",
              borderRadius: 6, color: "#64748b", fontSize: 11, cursor: "pointer", outline: "none" }}>
            <option value="es">🇪🇸 ES</option>
            <option value="en">🇬🇧 EN</option>
            <option value="pt">🇧🇷 PT</option>
          </select>

          <TbSep />

          {/* ── Grupo: Variables ── */}
          <TbPillBtn
            onClick={() => setVarsOpen(!varsOpen)}
            active={varsOpen}
            activeColor="#1e40af"
            title="Variables globales"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M5 2H3a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h2"/><path d="M9 2h2a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H9"/><path d="M5 7h4"/></svg>
            Variables
          </TbPillBtn>

          <TbPillBtn
            onClick={() => { setBdOpen(!bdOpen); if (!bdOpen) syncBdFromNodes(); }}
            active={bdOpen}
            activeColor="#065f46"
            title="Base de datos mock"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="7" cy="4" rx="5" ry="2"/><path d="M2 4v3c0 1.1 2.24 2 5 2s5-.9 5-2V4"/><path d="M2 7v3c0 1.1 2.24 2 5 2s5-.9 5-2V7"/></svg>
            BD
          </TbPillBtn>

          <TbSep />

          {/* ── Grupo: Archivo ── */}
          <div style={tbGroup}>
            <TbPillBtn onClick={() => {
              if (window.confirm("¿Limpiar el canvas? Se perderán los cambios no guardados.")) {
                setNodes([{ id: "start_1", type: "startingPoint", x: 100, y: 280, data: {} }]);
                setEdges([]); setBotName("Mi Bot WhatsApp");
                setVars([{ id: "v1", name: "nombre", defaultValue: "", value: "" }, { id: "v2", name: "telefono", defaultValue: "", value: "" }]);
              }
            }} title="Nuevo bot" danger>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M6 2v4M4 4l2-2 2 2"/><rect x="1.5" y="6" width="9" height="4.5" rx="1"/></svg>
              Nuevo
            </TbPillBtn>

            <TbPillBtn onClick={doExport} title="Exportar JSON">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M6 2v6M4 6l2 2 2-2"/><path d="M2 10h8"/></svg>
              JSON
            </TbPillBtn>

            <TbPillBtn onClick={() => fileRef.current?.click()} title="Importar JSON">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M6 8V2M4 4l2-2 2 2"/><path d="M2 10h8"/></svg>
              <input ref={fileRef} type="file" accept=".json" onChange={doImport} style={{ display: "none" }} />
            </TbPillBtn>

            <TbPillBtn onClick={doExportZip} title="Exportar ZIP (incluye imágenes)" activeColor="#6d28d9" accent>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1.5" y="1.5" width="9" height="9" rx="1.5"/><path d="M4.5 1.5v9M4.5 3.5h1.5M4.5 5.5h1.5M4.5 7.5h1.5"/></svg>
              ZIP
            </TbPillBtn>

            <TbPillBtn onClick={() => zipFileRef.current?.click()} title="Importar ZIP" accent>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M6 8V2M4 4l2-2 2 2"/><path d="M2 10h8"/></svg>
              <input ref={zipFileRef} type="file" accept=".zip" onChange={doImportZip} style={{ display: "none" }} />
            </TbPillBtn>

            <TbPillBtn onClick={doExportText} title="Exportar texto plano">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 3h8M2 6h6M2 9h4"/></svg>
              Texto
            </TbPillBtn>

            <TbSep />

            <TbPillBtn onClick={() => cytFileRef.current?.click()} title="Importar Bot CyT (4 archivos JSON)" activeColor="#b45309" accent>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9.5L6 2l4 7.5H2z"/><path d="M6 5.5v2"/><circle cx="6" cy="8.5" r="0.4" fill="currentColor" stroke="none"/></svg>
              Bot CyT
              <input ref={cytFileRef} type="file" multiple accept=".json" onChange={doImportCyt} style={{ display: "none" }} />
            </TbPillBtn>

            <TbPillBtn onClick={doExportCyt} title="Exportar bot como ZIP para el Generador CyT" activeColor="#b45309" accent>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9.5L6 2l4 7.5H2z"/><path d="M6 5.5v2"/><circle cx="6" cy="8.5" r="0.4" fill="currentColor" stroke="none"/></svg>
              CyT ZIP
            </TbPillBtn>
          </div>

          <TbSep />

          {/* ── Emulador ── */}
          {onSendToEmulator && (
            <button
              onClick={() => onSendToEmulator({ name: botName, nodes, edges, vars, bd, aiConfig, lang, v: "2.0" })}
              style={{
                height: 28, padding: "0 12px", borderRadius: 6, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #065f46, #047857)",
                color: "#6ee7b7", fontSize: 11, fontWeight: 600,
                display: "flex", alignItems: "center", gap: 6,
                fontFamily: "'Segoe UI', system-ui, sans-serif",
                boxShadow: "0 0 0 1px rgba(6,95,70,0.5)",
              }}>
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="1" width="8" height="12" rx="1.5"/>
                <circle cx="7" cy="11" r="0.7" fill="currentColor" stroke="none"/>
                <path d="M5 3h4"/>
              </svg>
              Emulador
            </button>
          )}

          {/* ── Publicar en motor (deshabilitado hasta integración con tercero) ── */}
          {/* <PublishButton flow={{ name: botName, nodes, edges, vars, lang, v: "2.0" }} showToast={showToast} /> */}
        </div>

        {/* PANEL DE VARIABLES (dropdown) */}
        {varsOpen && (
          <div style={{
            position: "absolute", top: 44, right: propsOpen ? 310 : 0,
            width: 290, maxHeight: "55vh", background: "#0f172a",
            border: "1px solid #1e293b", borderRadius: "0 0 0 10px",
            zIndex: 14, overflowY: "auto", padding: 14,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h4 style={{ margin: 0, color: "#e2e8f0", fontSize: 12 }}>{"{ }"} {t("ui.variables")}</h4>
              <button onClick={() => addVar()}
                style={{ background: "#1e40af", border: "none", borderRadius: 5,
                  color: "white", padding: "3px 8px", cursor: "pointer", fontSize: 10 }}>
                + {t("ui.addVariable")}
              </button>
            </div>
            {vars.map(v => (
              <div key={v.id} style={{ display: "flex", gap: 4, marginBottom: 6, alignItems: "center" }}>
                <input value={v.name} onChange={e => setVars(p => p.map(vr => vr.id === v.id ? { ...vr, name: e.target.value } : vr))}
                  style={{ ...inputSt, flex: 1, color: "#38bdf8", fontFamily: "monospace" }} />
                <input value={v.defaultValue}
                  onChange={e => setVars(p => p.map(vr => vr.id === v.id ? { ...vr, defaultValue: e.target.value } : vr))}
                  placeholder="def" style={{ ...inputSt, width: 60 }} />
                <button onClick={() => setVars(p => p.filter(vr => vr.id !== v.id))} style={delBtnSt}>×</button>
              </div>
            ))}
            <div style={{ fontSize: 9, color: "#475569", marginTop: 6 }}>{t("ui.varHint")}</div>
          </div>
        )}

        {/* PANEL BD (dropdown) */}
        {bdOpen && (
          <div style={{
            position: "absolute", top: 44, right: propsOpen ? 310 : 0,
            width: 340, maxHeight: "65vh", background: "#0f172a",
            border: "1px solid #1e293b", borderRadius: "0 0 0 10px",
            zIndex: 14, overflowY: "auto", padding: 14,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h4 style={{ margin: 0, color: "#e2e8f0", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="#6ee7b7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="7" cy="4" rx="5" ry="2"/><path d="M2 4v3c0 1.1 2.24 2 5 2s5-.9 5-2V4"/><path d="M2 7v3c0 1.1 2.24 2 5 2s5-.9 5-2V7"/></svg>
                Base de Datos Mock
              </h4>
              <button onClick={syncBdFromNodes} title="Sincronizar procedimientos desde nodos del canvas" style={{
                background: "#065f46", border: "none", borderRadius: 5,
                color: "#6ee7b7", padding: "3px 8px", cursor: "pointer", fontSize: 10,
              }}>
                Sync
              </button>
            </div>

            {bd.procedures.length === 0 && (
              <div style={{ color: "#475569", fontSize: 11, textAlign: "center", padding: "12px 0" }}>
                Sin procedimientos. Agrega nodos BD al canvas y presiona Sync.
              </div>
            )}

            {bd.procedures.map(proc => (
              <div key={proc.id} style={{ marginBottom: 10, border: "1px solid #1e293b", borderRadius: 7, overflow: "hidden" }}>
                {/* Header del procedimiento */}
                <div
                  onClick={() => setBdCollapsed(p => ({ ...p, [proc.id]: !p[proc.id] }))}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "6px 10px", background: "#0c1a2e", cursor: "pointer",
                  }}
                >
                  <span style={{ color: "#6ee7b7", fontSize: 11, fontWeight: 600, fontFamily: "monospace" }}>
                    {bdCollapsed[proc.id] ? "▶" : "▼"} {proc.name}
                  </span>
                  <span style={{ color: "#475569", fontSize: 10 }}>
                    {proc.outputFields?.map(f => f.name).join(", ") || "sin campos"}
                  </span>
                </div>

                {/* Cuerpo: filas */}
                {!bdCollapsed[proc.id] && (
                  <div style={{ padding: "8px 10px", background: "#0a0e1a" }}>
                    {proc.rows.map((row, ri) => (
                      <div key={ri} style={{ display: "flex", gap: 4, marginBottom: 5, alignItems: "center" }}>
                        {(proc.outputFields || []).map(field => (
                          <div key={field.name} style={{ flex: 1 }}>
                            <div style={{ color: "#475569", fontSize: 9, marginBottom: 2 }}>{field.name}</div>
                            <input
                              value={row[field.name] ?? ""}
                              onChange={e => {
                                const val = e.target.value;
                                setBd(prev => ({
                                  ...prev,
                                  procedures: prev.procedures.map(p =>
                                    p.id !== proc.id ? p : {
                                      ...p,
                                      rows: p.rows.map((r, i) => i !== ri ? r : { ...r, [field.name]: val }),
                                    }
                                  ),
                                }));
                              }}
                              style={{ ...inputSt, width: "100%", fontFamily: "monospace", fontSize: 10 }}
                            />
                          </div>
                        ))}
                        <button
                          onClick={() => setBd(prev => ({
                            ...prev,
                            procedures: prev.procedures.map(p =>
                              p.id !== proc.id ? p : { ...p, rows: p.rows.filter((_, i) => i !== ri) }
                            ),
                          }))}
                          style={{ ...delBtnSt, marginTop: 14, flexShrink: 0 }}
                        >×</button>
                      </div>
                    ))}
                    <button
                      onClick={() => setBd(prev => ({
                        ...prev,
                        procedures: prev.procedures.map(p =>
                          p.id !== proc.id ? p : { ...p, rows: [...p.rows, {}] }
                        ),
                      }))}
                      style={addBtnSt}
                    >+ Fila</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* SVG CANVAS */}
        <svg ref={canvasRef}
          style={{ width: "100%", height: "100%", cursor: panning ? "grabbing" : "default" }}
          onMouseDown={onCanvasDown} onMouseMove={onCanvasMove} onMouseUp={onCanvasUp}
          onWheel={onWheel}
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            const nt = e.dataTransfer.getData("nodeType");
            if (nt && NDEFS[nt]) {
              const cp = s2c(e.clientX, e.clientY);
              setNodes(p => [...p, {
                id: uid(), type: nt, x: cp.x - 120, y: cp.y - 30,
                data: JSON.parse(JSON.stringify(NDEFS[nt].data)),
              }]);
              showToast(`✅ ${t(`node.${nt}`)} agregado`);
            }
          }}
        >
          <defs>
            <pattern id="dots" width={20} height={20} patternUnits="userSpaceOnUse"
              patternTransform={`translate(${offset.x % (20 * scale)},${offset.y % (20 * scale)}) scale(${scale})`}>
              <circle cx={10} cy={10} r={0.7} fill="#1e293b" />
            </pattern>
          </defs>

          {/* Fondo */}
          <rect className="cbg" width="100%" height="100%" fill="#0a0e1a" />
          <rect className="cbg" width="100%" height="100%" fill="url(#dots)" />

          {/* Grupo transformado */}
          {(() => {
            // Viewport culling: solo renderizar lo que está visible en pantalla
            const cvs = canvasRef.current;
            const cw = cvs ? cvs.clientWidth : window.innerWidth;
            const ch = cvs ? cvs.clientHeight : window.innerHeight;
            const pad = 300 / scale; // margen extra en coords de canvas
            const vx0 = (-offset.x / scale) - pad;
            const vy0 = (-offset.y / scale) - pad;
            const vx1 = ((cw - offset.x) / scale) + pad;
            const vy1 = ((ch - offset.y) / scale) + pad;
            const visibleNodes = nodes.filter(n => {
              const h = 300; // altura máxima estimada (evita calcular nodeH en el filtro)
              return n.x + NW > vx0 && n.x < vx1 && n.y + h > vy0 && n.y < vy1;
            });
            const visibleIds = new Set(visibleNodes.map(n => n.id));
            // Un edge es visible si alguno de sus extremos está en viewport
            const visibleEdges = edges.filter(e => visibleIds.has(e.src) || visibleIds.has(e.tgt));
            return (
              <g transform={`translate(${offset.x},${offset.y}) scale(${scale})`}>
                {/* Edges */}
                {visibleEdges.map(renderEdge)}

                {/* Línea temporal de conexión */}
                {connFrom && (() => {
                  const fp = portPos(connFrom.nid, connFrom.pid, false);
                  return <line x1={fp.x} y1={fp.y} x2={mpos.x} y2={mpos.y}
                    stroke="#38bdf8" strokeWidth={2} strokeDasharray="6,3" opacity={0.6} />;
                })()}

                {/* Nodos */}
                {visibleNodes.map(renderNode)}
              </g>
            );
          })()}

          {/* Hint cuando no hay nodos (excepto start) */}
          {nodes.length <= 1 && (
            <text x="50%" y="50%" textAnchor="middle" fill="#334155" fontSize={14}
              fontFamily="'Segoe UI',system-ui,sans-serif">
              {t("ui.noNodes")} → {t("ui.connectHint")}
            </text>
          )}
        </svg>

        {/* Panel de propiedades */}
        {renderProps()}

        {/* Toast notification */}
        {toast && (
          <div style={{
            position: "absolute", bottom: 20, left: "50%", transform: "translateX(-50%)",
            background: "#1e293b", border: "1px solid #334155", borderRadius: 8,
            padding: "8px 16px", color: "#e2e8f0", fontSize: 12, zIndex: 50,
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
            animation: "fadeIn 0.2s ease",
          }}>
            {toast}
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateX(-50%) translateY(10px) } to { opacity:1; transform:translateX(-50%) translateY(0) } }
        ::-webkit-scrollbar { width: 5px }
        ::-webkit-scrollbar-track { background: transparent }
        ::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px }
        * { box-sizing: border-box }
        body { margin: 0; padding: 0 }
      `}</style>

      {/* ── Modal inspector de flujo ── */}
      {flowInspOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
          zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#0f172a", border: "1px solid #334155", borderRadius: 12,
            width: 740, maxWidth: "96vw", maxHeight: "82vh",
            display: "flex", flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          }}>
            {/* Header */}
            <div style={{
              padding: "14px 20px", borderBottom: "1px solid #1e293b",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ fontSize: 16 }}>🔍</span>
              <span style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 14, flex: 1 }}>
                Inspector de flujo — {flowInspData?.botName || botName}
              </span>
              <span style={{ color: "#64748b", fontSize: 11 }}>
                {flowInspData?.stats?.reachableNodes || 0} nodos alcanzables
                {flowInspData?.stats?.unreachableNodes > 0 &&
                  ` · ⚠ ${flowInspData.stats.unreachableNodes} no alcanzables`}
              </span>
            </div>

            {/* Log area */}
            <div style={{
              flex: 1, overflowY: "auto", padding: "12px 16px",
              fontFamily: "'Cascadia Code','Consolas','Courier New',monospace",
              fontSize: 11, lineHeight: 1.7,
            }}>
              {flowInspLines.map((line, i) => (
                <div key={i} style={{
                  color: {
                    header: "#818cf8",
                    step:   "#e2e8f0",
                    detail: "#64748b",
                    edge:   "#34d399",
                    warn:   "#fbbf24",
                  }[line.level] || "#94a3b8",
                  fontWeight: line.level === "step" ? 700 : 400,
                  marginTop:  line.level === "step" ? 4 : 0,
                  whiteSpace: "pre",
                }}>
                  {line.text}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{
              padding: "12px 20px", borderTop: "1px solid #1e293b",
              display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center",
            }}>
              <button
                onClick={async () => {
                  const zip = new JSZip();
                  const imgFolder = zip.folder("images");
                  let imgIdx = 0;

                  // Clonar para no mutar el estado
                  const fullData = JSON.parse(JSON.stringify(flowInspData));

                  // Extraer imágenes embebidas de nodos alcanzables y no alcanzables
                  const allDataObjs = [
                    ...(fullData.traversal || []).map(s => s.data),
                    ...(fullData.unreachable || []).map(n => n.data),
                  ];
                  for (const d of allDataObjs) {
                    if (!d) continue;
                    for (const field of ["imageUrl", "mediaUrl", "headerMediaUrl"]) {
                      const val = d[field];
                      if (!val || !val.startsWith("data:")) continue;
                      const [meta, b64] = val.split(",");
                      const mime = (meta.match(/data:([^;]+);base64/) || [])[1] || "image/png";
                      const ext  = mime.split("/")[1]?.split("+")[0] || "png";
                      const fileName = `img_${imgIdx++}.${ext}`;
                      imgFolder.file(fileName, b64, { base64: true });
                      d[field] = `images/${fileName}`;
                    }
                  }

                  const summaryText = flowInspLines.map(l => l.text).join("\n");
                  zip.file("resumen.txt", summaryText);
                  zip.file("flujo_completo.json", JSON.stringify(fullData, null, 2));
                  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = `${(flowInspData?.botName || botName || "flujo").replace(/\s+/g, "_")}_inspeccion.zip`;
                  a.click();
                }}
                style={{
                  padding: "7px 14px", borderRadius: 7, border: "1px solid #334155",
                  background: "transparent", color: "#94a3b8", cursor: "pointer", fontSize: 12,
                }}
              >
                Descargar ZIP
              </button>
              <button
                onClick={() => setFlowInspOpen(false)}
                style={{
                  padding: "7px 18px", borderRadius: 7, border: "none",
                  background: "#6366f1", color: "#fff",
                  cursor: "pointer", fontSize: 12, fontWeight: 600,
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Toolbar helpers ──────────────────────────────────────────
const tbGroup = {
  display: "flex", alignItems: "center", gap: 2,
};

function TbBtn({ onClick, disabled, title, children }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} style={{
      width: 28, height: 28, borderRadius: 6, border: "1px solid transparent",
      background: "transparent", color: disabled ? "#1e293b" : "#475569",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "background 0.1s, color 0.1s",
    }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = "#1e293b"; e.currentTarget.style.color = "#94a3b8"; } }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = disabled ? "#1e293b" : "#475569"; }}
    >{children}</button>
  );
}

function TbPillBtn({ onClick, title, children, active, activeColor, accent, danger }) {
  const base = danger ? "#7f1d1d" : accent ? "#2e1065" : "#0f172a";
  const baseText = danger ? "#fca5a5" : accent ? "#a78bfa" : "#64748b";
  const activeBg = activeColor || (accent ? "#4c1d95" : "#1e40af");
  return (
    <button onClick={onClick} title={title} style={{
      height: 28, padding: "0 9px", borderRadius: 6,
      border: `1px solid ${active ? activeBg : (danger ? "#991b1b" : accent ? "#4c1d95" : "#1e293b")}`,
      background: active ? activeBg : base,
      color: active ? "#fff" : baseText,
      fontSize: 11, fontWeight: 500, cursor: "pointer",
      display: "flex", alignItems: "center", gap: 5,
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      transition: "all 0.1s", whiteSpace: "nowrap",
    }}
      onMouseEnter={e => { e.currentTarget.style.background = active ? activeBg : (danger ? "#991b1b" : accent ? "#4c1d95" : "#1e293b"); e.currentTarget.style.color = "#e2e8f0"; }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? activeBg : base; e.currentTarget.style.color = active ? "#fff" : baseText; }}
    >{children}</button>
  );
}

function TbSep() {
  return <div style={{ width: 1, height: 20, background: "#1e293b", margin: "0 4px", flexShrink: 0 }} />;
}

// PublishButton — deshabilitado hasta integración con el tercero
// function PublishButton({ flow, showToast }) { ... }
// ─────────────────────────────────────────────────────────────
const delBtnSt = {
  background: "#ef4444", border: "none", borderRadius: 4,
  color: "white", width: 22, height: 22, cursor: "pointer", fontSize: 13,
  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
};
const addBtnSt = {
  background: "none", border: "1px dashed #475569", borderRadius: 5,
  color: "#94a3b8", padding: "4px 10px", cursor: "pointer",
  fontSize: 10, width: "100%", marginBottom: 10,
};

const suggBtnSt = {
  display: "block", width: "100%", marginBottom: 6,
  padding: "5px 10px", background: "rgba(56,189,248,0.08)",
  border: "1px dashed #38bdf8", borderRadius: 5,
  color: "#38bdf8", fontSize: 10, cursor: "pointer", textAlign: "left",
};
